import React, { useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import TradingViewChart from './TradingViewChart_new';
import ResearchDisclaimer from '../../../Components/Common/ResearchDisclaimer';
import ReportMarketGraph from './ReportMarketGraph';
import { Container, Row, Col, Card, CardBody, Button, Input, Label, Form, FormGroup } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

// Import logo
import logoLight from "../../../assets/images/logo-light.png";

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const timeframes = ['', '1h', '4h', '1d', '2d', '7d', '30d', '180d'];

// Fixed model groups (display label -> internal value used by backend)
const PRIMARY_MODELS = [
  { label: 'Claude-premium', value: 'Claude-premium' },
  { label: 'Gemini-premium', value: 'Gemini-premium' },
  { label: 'gpt-premium', value: 'gpt-premium' },
];

const BASIC_MODELS = [
  { label: 'Claude', value: 'Claude' },
  { label: 'Gemini', value: 'Gemini' },
  { label: 'gpt', value: 'gpt' },
  // Custom rule-based
  { label: 'BIS', value: 'BIS' },
];

// Backtest strategies
const BACKTEST_STRATEGIES = [
  { value: 'long-consensus', label: 'Long Only (Bullish Consensus)', description: 'Buy when ALL selected models agree bullish' },
  { value: 'long-short-consensus', label: 'Long & Short (All Models Same Signal)', description: 'Long when all bullish, short when all bearish' },
  { value: 'long-any-bullish', label: 'Long on Any Bullish', description: 'Buy when any model predicts bullish' },
  { value: 'long-short-majority', label: 'Long/Short Majority', description: 'Long if majority bullish, short if majority bearish' },
];

// tv.js script is loaded by TradingViewChart component

/**
 * Sort predictions based on score availability
 * If all predictions have valid score data, sort by score (high to low)
 * Otherwise, sort by creation time (latest to oldest)
 */
function sortPredictions(predictions) {
  if (!Array.isArray(predictions) || predictions.length === 0) {
    return predictions;
  }
  
  // Check if ALL predictions have valid score data
  const allHaveValidScores = predictions.every(p => 
    p.performance !== null && 
    p.performance !== undefined && 
    p.performance !== '' &&
    p.performance !== 0 &&
    !isNaN(parseFloat(p.performance))
  );
  
  if (allHaveValidScores) {
    // Sort by score: high to low
    return [...predictions].sort((a, b) => {
      const scoreA = parseFloat(a.performance) || 0;
      const scoreB = parseFloat(b.performance) || 0;
      return scoreB - scoreA; // Descending order (high to low)
    });
  } else {
    // Sort by creation time: latest to oldest
    return [...predictions].sort((a, b) => {
      const dateA = new Date(a.create_date || 0);
      const dateB = new Date(b.create_date || 0);
      return dateB - dateA; // Descending order (newest first)
    });
  }
}

/**
 * Sort predictions by specific column and order
 */
function sortPredictionsByColumn(predictions, sortBy, sortOrder) {
  if (!Array.isArray(predictions) || predictions.length === 0 || !sortBy) {
    return predictions;
  }
  
  const sorted = [...predictions].sort((a, b) => {
    let valueA, valueB;
    
    switch (sortBy) {
      case 'model':
        valueA = (a.model || '').toString().toLowerCase();
        valueB = (b.model || '').toString().toLowerCase();
        break;
      case 'create_date':
        valueA = new Date(a.create_date || 0);
        valueB = new Date(b.create_date || 0);
        break;
      case 'target_date':
        valueA = new Date(a.target_date || 0);
        valueB = new Date(b.target_date || 0);
        break;
      case 'prediction_low':
        valueA = parseFloat(a.prediction_low) || 0;
        valueB = parseFloat(b.prediction_low) || 0;
        break;
      case 'current_price':
        valueA = parseFloat(a.current_price ?? a.currentPrice ?? a.price_at_prediction ?? a.priceAtPrediction ?? a.price) || 0;
        valueB = parseFloat(b.current_price ?? b.currentPrice ?? b.price_at_prediction ?? b.priceAtPrediction ?? b.price) || 0;
        break;
      case 'prediction_high':
        valueA = parseFloat(a.prediction_high) || 0;
        valueB = parseFloat(b.prediction_high) || 0;
        break;
      case 'confidence':
        valueA = parseFloat(a.confidence) || 0;
        valueB = parseFloat(b.confidence) || 0;
        break;
      case 'actual_price':
        valueA = parseFloat(a.actual_price) || 0;
        valueB = parseFloat(b.actual_price) || 0;
        break;
      case 'actual_low':
        valueA = parseFloat(a.actual_low) || 0;
        valueB = parseFloat(b.actual_low) || 0;
        break;
      case 'actual_high':
        valueA = parseFloat(a.actual_high) || 0;
        valueB = parseFloat(b.actual_high) || 0;
        break;
      case 'performance':
        valueA = parseFloat(a.performance) || 0;
        valueB = parseFloat(b.performance) || 0;
        break;
      case 'signal':
        valueA = ((a.signal || '') || getFallbackSignal(a)).toString().toLowerCase();
        valueB = ((b.signal || '') || getFallbackSignal(b)).toString().toLowerCase();
        break;
      case 'actual_dir': {
  // Sort by actual percent change (signed). Missing values sort last.
  const pctA = getActualChangePercent(a);
  const pctB = getActualChangePercent(b);
  // Use very small value for missing to push to end on desc
  valueA = (typeof pctA === 'number' && isFinite(pctA)) ? pctA : Number.NEGATIVE_INFINITY;
  valueB = (typeof pctB === 'number' && isFinite(pctB)) ? pctB : Number.NEGATIVE_INFINITY;
        break;
      }
      case 'timeframe':
        valueA = (a.timeframe || '').toString().toLowerCase();
        valueB = (b.timeframe || '').toString().toLowerCase();
        break;
      default:
        return 0;
    }

    // Handle comparison
    if (valueA === valueB) return 0;
    
    let comparison;
    if (typeof valueA === 'string') {
      comparison = valueA.localeCompare(valueB);
    } else if (valueA instanceof Date) {
      comparison = valueA - valueB;
    } else {
      comparison = valueA - valueB;
    }

    return sortOrder === 'asc' ? comparison : -comparison;
  });
  
  return sorted;
}

export default function ReportPage() {
  const LAST_SEARCH_KEY = 'report:lastSearch';
  // Mode 1: Current predictions (default)
  // Mode 2: Historical accuracy (actual vs prediction ranges)
  // Mode 3: Model score comparison
  const [mode, setMode] = useState(1);
  const [symbol, setSymbol] = useState('BTC');
    const [timeframe, setTimeframe] = useState('1h');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState([]);
  const [availableSymbols, setAvailableSymbols] = useState(['BTC', 'ETH', 'TQQQ', 'UGL', 'IBIT', 'SOXL', 'FAS', 'TMF', 'LABU', 'UCO', 'IONQ', 'CEG', 'QBTS', 'MP', 'GOOG', 'TSLA', 'AMZN', 'AAPL', 'MSFT', 'NVDA','WSP.TO', 'META', 'FTS', 'SPY', 'PAXG', 'XRP']); // Start with all configured symbols
  const [symbolsLoading, setSymbolsLoading] = useState(true);
  // Derived, de-duplicated predictions passed to ReportMarketGraph (avoid legend clutter)
  const dedupedRows = useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return rows;
    // Key fields likely defining uniqueness
    const KEY_FIELDS = ['model','symbol','timeframe','create_date','target_date','prediction_low','prediction_high'];
    const seen = new Set();
    const result = [];
    for (const r of rows) {
      try {
        const keyParts = KEY_FIELDS.map(k => (r && r[k]) != null ? String(r[k]) : '');
        const key = keyParts.join('|');
        if (!seen.has(key)) {
          seen.add(key);
          result.push(r);
        }
      } catch { /* ignore */ }
    }
    // If large reduction occurs, log for diagnostics
    if (result.length !== rows.length) {
      console.log(`🔍 Dedupe reduced predictions from ${rows.length} to ${result.length}`);
    }
    return result;
  }, [rows]);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [tvReady, setTvReady] = useState(false);
  const [selectedModels, setSelectedModels] = useState([]);
  const [selectedHours, setSelectedHours] = useState([]);
  // Detect clean/embed mode from URL; hide options panel by default when enabled
  const computeIsClean = () => {
    try {
      const sp = new URLSearchParams(window.location.search || '');
      const clean = (sp.get('clean') || '').toString().toLowerCase();
      const ui = (sp.get('ui') || '').toString().toLowerCase();
      const embed = (sp.get('embed') || '').toString().toLowerCase();
      return clean === '1' || clean === 'true' || ui === 'clean' || ui === 'embed' || embed === '1' || embed === 'true';
    } catch { return false; }
  };
  const isCleanMode = useMemo(() => computeIsClean(), []);
  const [showOptionsPanel, setShowOptionsPanel] = useState(() => !computeIsClean());
  const [chartType, setChartType] = useState('tradingview'); // 'tradingview' or 'dashboard-crypto' - TradingView as default
  
  // Performance mode detection from URL
  const computeIsPerformanceMode = () => {
    try {
      const sp = new URLSearchParams(window.location.search || '');
      const perf = (sp.get('performance') || '').toString().toLowerCase();
      return perf === '1' || perf === 'true';
    } catch { return false; }
  };
  const [isPerformanceMode, setIsPerformanceMode] = useState(() => computeIsPerformanceMode());
  
  // Sorting state
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  // Chart focus selections
  const [mode2FocusedModels, setMode2FocusedModels] = useState([]); // array of model values
  const [mode3FocusedModels, setMode3FocusedModels] = useState([]);

  const tvWidgetRef = useRef(null);
  const shapeIdsRef = useRef([]);
  // Tracks the last query used to fetch data, to avoid duplicate requests
  const lastFetchKeyRef = useRef('');
  // Tracks an in-flight fetch key to prevent concurrent duplicates
  const pendingFetchKeyRef = useRef('');
  
  // Chart-specific state that only updates on Apply, not on every option change
  const [chartSymbol, setChartSymbol] = useState('BTC');
  const [chartTimeframe, setChartTimeframe] = useState('1d');

  // Mode 2 state: summary table + chart rows
  const [mode2Summary, setMode2Summary] = useState([]);
  const [mode2ChartRows, setMode2ChartRows] = useState([]);
  const [mode2DailyPnL, setMode2DailyPnL] = useState({});
  const [mode2ShowCumulativePnL, setMode2ShowCumulativePnL] = useState(false);
  const mode2RefetchedOnceRef = useRef(false);
  const mode2TriedNoModelRef = useRef(false);
  // Mode 2 toggles: Actual Price line and Actual Range band visibility (default: shown)
  const [mode2ShowActualPrice, setMode2ShowActualPrice] = useState(true);
  const [mode2ShowActualRange, setMode2ShowActualRange] = useState(true);

  // Mode 3 state: aggregated model scores
  const [mode3Data, setMode3Data] = useState([]);
  const [mode3ChartRows, setMode3ChartRows] = useState([]);
  const mode3RefetchedOnceRef = useRef(false);
  const mode3TriedNoModelRef = useRef(false);

  // Backtest state
  const [backtestStrategy, setBacktestStrategy] = useState('long-consensus');
  const [showCumulativeProfile, setShowCumulativeProfile] = useState(false);
  const [backtestResults, setBacktestResults] = useState(null);
  const [backtestPerformed, setBacktestPerformed] = useState(false);

  // Available 4-hour prediction points for 4h timeframe
  const hourOptions = ['0h', '4h', '8h', '12h', '16h', '20h'];

  // Trading fee per transaction (0.2%)
  const TRADING_FEE = 0.002;

  // Format hour labels for display
  const formatHourLabel = (hour) => {
    const hourMap = {
      '0h': '0:00',
      '4h': '4:00', 
      '8h': '8:00',
      '12h': '12:00',
      '16h': '16:00',
      '20h': '20:00'
    };
    return hourMap[hour] || hour;
  };

  const tvInterval = useMemo(() => {
    // Mapping per requirement: 4h -> 5m, 1d -> 5m, all others -> 1D
    const map = { '4h': '5', '1d': '5' };
    return map[chartTimeframe] || 'D';
  }, [chartTimeframe]);

  const getTvInterval = (tf) => {
    // Mapping per requirement: 4h -> 5m, 1d -> 5m, all others -> 1D
    const map = { '4h': '5', '1d': '5' };
    return map[tf] || 'D';
  };

  const tvSymbol = useMemo(() => {
    // Use explicit price tickers to avoid market-cap symbols
    if (chartSymbol === 'SPY') return 'AMEX:SPY';
    if (chartSymbol === 'TQQQ') return 'NASDAQ:TQQQ';
    if (chartSymbol === 'UGL') return 'AMEX:UGL';
    if (chartSymbol === 'SOXL') return 'AMEX:SOXL';
    if (chartSymbol === 'FAS') return 'AMEX:FAS';
    if (chartSymbol === 'TMF') return 'AMEX:TMF';
    if (chartSymbol === 'LABU') return 'AMEX:LABU';
    if (chartSymbol === 'UCO') return 'AMEX:UCO';
    if (chartSymbol === 'IBIT') return 'NASDAQ:IBIT';
    const map = {
      BTC: 'COINBASE:BTCUSD',
      ETH: 'COINBASE:ETHUSD',
      PAXG: 'COINBASE:PAXGUSD',
      XRP: 'COINBASE:XRPUSD',
      SOL: 'COINBASE:SOLUSD',
      AVAX: 'COINBASE:AVAXUSD',
      ADA: 'COINBASE:ADAUSD',
      ARB: 'COINBASE:ARBUSD',
      LINK: 'COINBASE:LINKUSD',
      UNI: 'COINBASE:UNIUSD',
      DOGE: 'COINBASE:DOGEUSD',
      SPY: 'SPY',
      TQQQ: 'NASDAQ:TQQQ',
      UGL: 'AMEX:UGL',
      IONQ: 'NYSE:IONQ',
      CEG: 'NASDAQ:CEG',
      QBTS: 'NYSE:QBTS',
      MP: 'NYSE:MP',
      GOOG: 'NASDAQ:GOOG',
      TSLA: 'NASDAQ:TSLA',
      AMZN: 'NASDAQ:AMZN',
      AAPL: 'NASDAQ:AAPL',
      MSFT: 'NASDAQ:MSFT',
      NVDA: 'NASDAQ:NVDA',
      'WSP.TO': 'TSX:WSP',
      META: 'NASDAQ:META',
      FTS: 'NYSE:FTS'
    };
    return map[chartSymbol] || `${chartSymbol}USD`;
  }, [chartSymbol]);

  // Debug: log computed TradingView props whenever they change
  useEffect(() => {
    // Debug logging removed - chart updates are now controlled by Apply button
  }, []);

  // Fetch available symbols from backend API
  useEffect(() => {
    console.log('🔄 useEffect triggered for symbol fetching');
    const fetchSymbols = async () => {
      try {
        setSymbolsLoading(true);
        console.log('Fetching symbols from:', `${API}/api/ai-predictions/supported-assets`);
        const response = await axios.get(`${API}/api/ai-predictions/supported-assets`);
        console.log('Symbols API response:', response.data);
        // Handle different response formats
        let symbolsObj = null;
        if (response.data && response.data.data && response.data.data.symbols) {
          // Format: { success: true, data: { symbols: {...} } }
          symbolsObj = response.data.data.symbols;
        } else if (response.data && response.data.symbols) {
          // Format: { symbols: {...} } or { success: true, data: { symbols: {...} } }
          symbolsObj = response.data.symbols;
        } else if (response.data && typeof response.data === 'object' && Object.keys(response.data).some(key => key.toUpperCase() === key)) {
          // Direct symbols object
          symbolsObj = response.data;
        }
        
        if (symbolsObj && typeof symbolsObj === 'object') {
          const symbolsList = Object.keys(symbolsObj);
          // Ensure all configured symbols are always included even if backend omits them
          const ALL_SYMBOLS = ['BTC', 'ETH', 'TQQQ', 'UGL', 'IBIT', 'SOXL', 'FAS', 'TMF', 'LABU', 'UCO', 'IONQ', 'CEG', 'QBTS', 'MP', 'GOOG', 'TSLA', 'AMZN', 'AAPL', 'MSFT', 'NVDA','WSP.TO', 'META', 'FTS', 'SPY', 'PAXG', 'XRP'];
          ALL_SYMBOLS.forEach(s => { if (!symbolsList.includes(s)) symbolsList.push(s); });
          console.log('Parsed symbols:', symbolsList);
          setAvailableSymbols(symbolsList);
        } else {
          console.warn('Unexpected response structure, using fallback');
          setAvailableSymbols(['BTC', 'ETH', 'TQQQ', 'UGL', 'IBIT', 'SOXL', 'FAS', 'TMF', 'LABU', 'UCO', 'IONQ', 'CEG', 'QBTS', 'MP', 'GOOG', 'TSLA', 'AMZN', 'AAPL', 'MSFT', 'NVDA','WSP.TO', 'META', 'FTS', 'SPY', 'PAXG', 'XRP']);
        }
      } catch (error) {
        console.error('Error fetching symbols:', error);
        // Fallback to hardcoded symbols if API fails
        setAvailableSymbols(['BTC', 'ETH', 'TQQQ', 'UGL', 'IBIT', 'SOXL', 'FAS', 'TMF', 'LABU', 'UCO', 'IONQ', 'CEG', 'QBTS', 'MP', 'GOOG', 'TSLA', 'AMZN', 'AAPL', 'MSFT', 'NVDA','WSP.TO', 'META', 'FTS', 'SPY', 'PAXG', 'XRP']);
      } finally {
        setSymbolsLoading(false);
      }
    };

    fetchSymbols();
  }, []);

  // Debug: log when availableSymbols changes
  useEffect(() => {
    console.log('📊 availableSymbols updated:', availableSymbols);
    console.log('📊 symbolsLoading:', symbolsLoading);
  }, [availableSymbols, symbolsLoading]);

  const getTvSymbol = (sym) => {
    if (sym === 'SPY') return 'AMEX:SPY';
    if (sym === 'TQQQ') return 'NASDAQ:TQQQ';
    if (sym === 'UGL') return 'AMEX:UGL';
    if (sym === 'SOXL') return 'AMEX:SOXL';
    if (sym === 'FAS') return 'AMEX:FAS';
    if (sym === 'UCO') return 'AMEX:UCO';
    if (sym === 'WSP.TO') return 'TSX:WSP';
    if (sym === 'IBIT') return 'NASDAQ:IBIT';
    const map = {
      BTC: 'COINBASE:BTCUSD',
      ETH: 'COINBASE:ETHUSD',
      PAXG: 'COINBASE:PAXGUSD',
      XRP: 'COINBASE:XRPUSD',
      SOL: 'COINBASE:SOLUSD',
      AVAX: 'COINBASE:AVAXUSD',
      ADA: 'COINBASE:ADAUSD',
      ARB: 'COINBASE:ARBUSD',
      LINK: 'COINBASE:LINKUSD',
      UNI: 'COINBASE:UNIUSD',
      DOGE: 'COINBASE:DOGEUSD',
      SPY: 'SPY',
      TQQQ: 'NASDAQ:TQQQ',
      UGL: 'AMEX:UGL'
    };
    return map[sym] || `${sym}USD`;
  };

  // Format a date string for display: if it contains time (HH:MM:SS), trim seconds -> HH:MM
  const formatDisplayDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return dateStr;
    // Expect formats like 'YYYY-MM-DD' or 'YYYY-MM-DD HH:MM:SS'
    if (dateStr.includes(' ')) {
      const [d, t] = dateStr.split(' ');
      if (t && t.includes(':')) {
        const parts = t.split(':');
        if (parts.length >= 2) {
          const hh = parts[0] || '00';
          const mm = parts[1] || '00';
          return `${d} ${hh}:${mm}`;
        }
      }
    }
    return dateStr;
  };

  // Check if a date string falls on Saturday (6) or Sunday (0)
  const isWeekend = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return false;
    try {
      // Extract just the date part (YYYY-MM-DD) - handle both space and T separators
      let dateOnly = dateStr;
      if (dateStr.includes(' ')) {
        dateOnly = dateStr.split(' ')[0];
      } else if (dateStr.includes('T')) {
        dateOnly = dateStr.split('T')[0];
      }
      const date = new Date(dateOnly + 'T00:00:00Z'); // Parse as UTC
      const dayOfWeek = date.getUTCDay();
      return dayOfWeek === 0 || dayOfWeek === 6; // Sunday (0) or Saturday (6)
    } catch (e) {
      return false;
    }
  };

  const [errorMsg, setErrorMsg] = useState('');
  // Combined model list for UI
  const ALL_MODELS = useMemo(() => [...PRIMARY_MODELS, ...BASIC_MODELS], []);
  const LABEL_TO_VALUE = useMemo(() => {
    const map = new Map();
    ALL_MODELS.forEach(m => {
      map.set((m.label || '').toLowerCase(), m.value);
      map.set((m.value || '').toLowerCase(), m.value);
    });
    return map;
  }, [ALL_MODELS]);
  const VALUE_TO_LABEL = useMemo(() => {
    const obj = {};
    ALL_MODELS.forEach(m => { obj[m.value] = m.label; });
    return obj;
  }, [ALL_MODELS]);
  
  // Reorder available symbols to put BTC, ETH, then all ETFs and stocks in priority order
  const sortedAvailableSymbols = useMemo(() => {
    if (!Array.isArray(availableSymbols)) return availableSymbols;
    const prioritySymbols = ['BTC', 'ETH', 'TQQQ', 'UGL', 'IBIT', 'SOXL', 'FAS', 'TMF', 'LABU', 'UCO', 'IONQ', 'CEG', 'QBTS', 'MP', 'GOOG', 'TSLA', 'AMZN', 'AAPL', 'MSFT', 'NVDA', 'WSP.TO', 'META', 'FTS', 'SPY'];
    const priority = availableSymbols.filter(s => prioritySymbols.includes(s));
    const others = availableSymbols.filter(s => !prioritySymbols.includes(s));
    return [...priority, ...others];
  }, [availableSymbols]);
  
  const toCanonicalModelValue = (s) => {
    if (!s) return '';
    const found = LABEL_TO_VALUE.get((s || '').toLowerCase());
    return found || (s || '').toLowerCase();
  };

  // Shared model color mapping across Mode 2 and Mode 3
  const getModelColor = (model) => {
    if (!model) return '#6c757d';
    const ml = (model || '').toString().toLowerCase();
  // Align color families with table styling:
  // - Claude (green), Gemini (amber/yellow), GPT (blue)
  if (ml.includes('claude-premium')) return '#43a047'; // Claude premium - green
  if (ml.includes('gemini-premium')) return '#f4b400'; // Gemini premium - amber/yellow
  if (ml.includes('gpt-premium')) return '#1e88e5'; // GPT premium - blue
  if (ml.includes('claude')) return '#81c784'; // Claude - green
  if (ml.includes('gemini')) return '#ffd54f'; // Gemini - amber
  if (ml.includes('gpt')) return '#1e88e5'; // GPT - blue
  if (ml.includes('BIS') || ml.includes('bis')) return '#8e24aa'; // Custom models - purple
    return '#6c757d'; // Default gray
  };

  // Build a stable key from URLSearchParams that only includes params affecting data
  const buildFetchKey = (params) => {
    try {
      const p = params instanceof URLSearchParams
        ? params
        : new URLSearchParams(params || window.location.search || '');
      const tf = p.get('timeframe') || '';
      // Only consider hours when timeframe is 4h
      const hrs = tf === '4h' ? (p.get('hours') || '') : '';
      const md = p.get('mode') || '1';
      const keyParts = [
        `symbol=${p.get('symbol') || ''}`,
        `from=${p.get('from') || ''}`,
        `to=${p.get('to') || ''}`,
        `timeframe=${tf}`,
        `model=${p.get('model') || ''}`,
        `hours=${hrs}`,
        `mode=${md}`,
      ];
      return keyParts.join('&');
    } catch (e) {
      return '';
    }
  };

  const fetchData = async (paramsOverride = null) => {
    setErrorMsg('');
    try {
      const params = paramsOverride instanceof URLSearchParams ? paramsOverride : (() => {
        const p = new URLSearchParams({ symbol });
        if (from) p.append('from', from);
        if (to) p.append('to', to);
        if (timeframe) p.append('timeframe', timeframe);
        if (Array.isArray(selectedModels) && selectedModels.length) p.append('model', selectedModels.join('+'));
        // Only include hours parameter when timeframe is 4h
        if (timeframe === '4h' && selectedHours && selectedHours.length) p.append('hours', selectedHours.join('+'));
        // Mode parameter
        if (mode) p.append('mode', String(mode));
        // Exclude reason for all report modes, as it's not displayed
        p.append('exclude_reason', 'true');
        return p;
      })();
      // Skip if this fetch would be identical to the last one
      const fetchKey = buildFetchKey(params);
  if (fetchKey && (fetchKey === lastFetchKeyRef.current || fetchKey === pendingFetchKeyRef.current)) {
        return;
      }
  pendingFetchKeyRef.current = fetchKey;
      setLoading(true);
  const res = await axios.get(`${API}/api/report-data?${params.toString()}`);
  const payload = res.data || {};
  const listAll = Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []);
  
  // Map API response fields to expected frontend field names
  const mappedList = listAll.map(r => ({
    ...r,
    create_date: r.create_date || r.input_time,  // Fallback to input_time if create_date not present
    target_date: r.target_date || r.valid_until  // Fallback to valid_until if target_date not present
  }));
  
  // Filter to only include predictions with actual prices
  const filteredList = mappedList.filter(r => {
    const actual = r.actual_price != null && r.actual_price !== '' ? parseFloat(r.actual_price) : null;
    return actual !== null && isFinite(actual);
  });
  // Branch by mode
  if (String(params.get('mode') || mode) === '2') {
    // Build per-prediction chart rows and per-model summary from unified rows
    const tfSel = params.get('timeframe') || timeframe;
    const chartRows = [];
    // First, build chart rows from all data (including without actual)
    for (const r of listAll) {
      const dt = r.create_date ? new Date(r.create_date) : null;
      if (!dt || isNaN(dt.getTime())) continue;
      // Bucket to date string
      const y = dt.getUTCFullYear();
      const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
      const d = String(dt.getUTCDate()).padStart(2, '0');
      let dateStr = `${y}-${m}-${d}`;
      if (tfSel === '4h') {
        const h = dt.getUTCHours();
        const slot = Math.floor(h / 4) * 4;
        const hh = String(slot).padStart(2, '0');
        dateStr = `${y}-${m}-${d} ${hh}:00:00`;
      }
  const actual = (r.actual_price != null && r.actual_price !== '') ? parseFloat(r.actual_price) : parseFloat(r.current_price);
  const actualLow = (r.actual_low != null && r.actual_low !== '') ? parseFloat(r.actual_low) : null;
  const actualHigh = (r.actual_high != null && r.actual_high !== '') ? parseFloat(r.actual_high) : null;
      const low = parseFloat(r.prediction_low);
      const high = parseFloat(r.prediction_high);
      const modelName = r.model || '';
      const score = (r.performance != null && r.performance !== '') ? parseFloat(r.performance) : null;
      // chart row per prediction
      chartRows.push({
        date: dateStr,
        actual_price: isFinite(actual) ? actual : null,
  actual_low: (typeof actualLow === 'number' && isFinite(actualLow)) ? actualLow : null,
  actual_high: (typeof actualHigh === 'number' && isFinite(actualHigh)) ? actualHigh : null,
        prediction_low: isFinite(low) ? low : null,
        prediction_high: isFinite(high) ? high : null,
        model: modelName,
        score
      });
    }
    // Then, build summary from filtered data (only with actual)
  const byModel = new Map();
    for (const r of filteredList) {
      const dt = r.create_date ? new Date(r.create_date) : null;
      if (!dt || isNaN(dt.getTime())) continue;
      // Bucket to date string
      const y = dt.getUTCFullYear();
      const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
      const d = String(dt.getUTCDate()).padStart(2, '0');
      let dateStr = `${y}-${m}-${d}`;
      if (tfSel === '4h') {
        const h = dt.getUTCHours();
        const slot = Math.floor(h / 4) * 4;
        const hh = String(slot).padStart(2, '0');
        dateStr = `${y}-${m}-${d} ${hh}:00:00`;
      }
      const actual = (r.actual_price != null && r.actual_price !== '') ? parseFloat(r.actual_price) : parseFloat(r.current_price);
      const actualLow = (r.actual_low != null && r.actual_low !== '') ? parseFloat(r.actual_low) : null;
      const actualHigh = (r.actual_high != null && r.actual_high !== '') ? parseFloat(r.actual_high) : null;
      const low = parseFloat(r.prediction_low);
      const high = parseFloat(r.prediction_high);
      const priceAtPred = parseFloat(r.current_price || r.currentPrice || r.price_at_prediction || r.priceAtPrediction || r.price);
      const modelName = r.model || '';
      const score = (r.performance != null && r.performance !== '') ? parseFloat(r.performance) : null;
      // summary by model
      if (!byModel.has(modelName)) byModel.set(modelName, { model: modelName, prediction_count: 0, scores: [], dates: [], neutral_predictions: 0, correct_direction: 0, wrong_direction: 0, predictionRanges: [], actualRanges: [], dailyPnL: {}, totalPnL: 0, previousDirection: 0 });
      const agg = byModel.get(modelName);
      agg.prediction_count += 1;
      if (isFinite(score)) agg.scores.push(score);
      agg.dates.push(dateStr);
      // Calculate prediction range as percentage from Price@Pred
      if (isFinite(low) && isFinite(high) && isFinite(priceAtPred) && priceAtPred !== 0) {
        const lowPct = ((low - priceAtPred) / priceAtPred) * 100;
        const highPct = ((high - priceAtPred) / priceAtPred) * 100;
        const predRange = highPct - lowPct;
        if (isFinite(predRange)) agg.predictionRanges.push(predRange);
      }
      // Calculate actual range as percentage from Price@Pred
      if (isFinite(actualLow) && isFinite(actualHigh) && isFinite(priceAtPred) && priceAtPred !== 0) {
        const actualLowPct = ((actualLow - priceAtPred) / priceAtPred) * 100;
        const actualHighPct = ((actualHigh - priceAtPred) / priceAtPred) * 100;
        const actualRange = actualHighPct - actualLowPct;
        if (isFinite(actualRange)) agg.actualRanges.push(actualRange);
      }
      // Calculate direction-based counters
      const hasActual = isFinite(actual);
      const hasLow = isFinite(low);
      const hasHigh = isFinite(high);
      if (hasActual && hasLow && hasHigh && isFinite(priceAtPred) && priceAtPred !== 0) {
        // Calculate prediction direction based on midpoint of range
        const predictionMidpoint = (low + high) / 2.0;
        const predictionChangeRatio = (predictionMidpoint - priceAtPred) / priceAtPred;
        let predictionDirection = 0;
        if (predictionChangeRatio > 0.005) {
          predictionDirection = 1; // bullish
        } else if (predictionChangeRatio < -0.005) {
          predictionDirection = -1; // bearish
        } else {
          predictionDirection = 0; // neutral
        }
        
        // Calculate actual direction
        const actualChangeRatio = (actual - priceAtPred) / priceAtPred;
        let actualDirection = 0;
        if (actualChangeRatio > 0.005) {
          actualDirection = 1; // bullish
        } else if (actualChangeRatio < -0.005) {
          actualDirection = -1; // bearish
        } else {
          actualDirection = 0; // neutral
        }
        
        // Count based on direction
        if (predictionDirection === 0) {
          agg.neutral_predictions += 1;
        } else if (predictionDirection === actualDirection) {
          agg.correct_direction += 1;
        } else {
          agg.wrong_direction += 1;
        }
        
        // Calculate P/L for this trade: only charge fee if direction changes
        if (predictionDirection !== 0) {
          const feeCharged = (predictionDirection === agg.previousDirection) ? 0 : TRADING_FEE;
          const pnlRatio = Math.abs((actual - priceAtPred) / priceAtPred) * predictionDirection * actualDirection - feeCharged;
          agg.totalPnL += pnlRatio;
          
          // Track daily P/L
          if (!agg.dailyPnL[dateStr]) {
            agg.dailyPnL[dateStr] = [];
          }
          agg.dailyPnL[dateStr].push(pnlRatio);
          
          // Update previous direction for this model
          agg.previousDirection = predictionDirection;
        } else {
          // Going to neutral/cash resets the position state
          agg.previousDirection = 0;
        }
      }
    }
    
    // Process daily cumulative P/L for each model
    const mode2DailyPnL = {};
    
    const summary = Array.from(byModel.values()).map(it => {
      const avg = it.scores.length ? (it.scores.reduce((a,b)=>a+b,0) / it.scores.length) : null;
      const avgPredRange = it.predictionRanges.length ? (it.predictionRanges.reduce((a,b)=>a+b,0) / it.predictionRanges.length) : null;
      const avgActualRange = it.actualRanges.length ? (it.actualRanges.reduce((a,b)=>a+b,0) / it.actualRanges.length) : null;
      // Compute true min/max by comparing strings (ISO-like) to ensure correct order
      let minDate = null, maxDate = null;
      for (const ds of it.dates) {
        if (!minDate || ds < minDate) minDate = ds;
        if (!maxDate || ds > maxDate) maxDate = ds;
      }
      const displayRange = (minDate && maxDate) ? `${formatDisplayDate(minDate)} to ${formatDisplayDate(maxDate)}` : null;
      
      // Calculate daily cumulative P/L for this model
      const sortedDates = Object.keys(it.dailyPnL).sort();
      let cumulativePnL = 0;
      const dailyCumulativePnL = [];
      for (const date of sortedDates) {
        const dayPnLArr = it.dailyPnL[date];
        const dayPnL = dayPnLArr.reduce((a, b) => a + b, 0);
        cumulativePnL += dayPnL;
        dailyCumulativePnL.push({ date, pnl: dayPnL, cumulativePnL });
      }
      
      // Store daily P/L data for charting
      mode2DailyPnL[it.model] = dailyCumulativePnL;
      
      return { 
        model: it.model, 
        prediction_count: it.prediction_count, 
        avg_score: avg, 
        avg_prediction_range: avgPredRange,
        avg_actual_range: avgActualRange,
        date_range: displayRange, 
        neutral_predictions: it.neutral_predictions, 
        correct_direction: it.correct_direction, 
        wrong_direction: it.wrong_direction,
        total_pnl: it.totalPnL
      };
    })
    // Order by average score descending (treat null/NaN as lowest)
    .sort((a, b) => {
      const as = Number.isFinite(a.avg_score) ? a.avg_score : -Infinity;
      const bs = Number.isFinite(b.avg_score) ? b.avg_score : -Infinity;
      return bs - as;
    });
    setMode2Summary(summary);
    setMode2ChartRows(chartRows);
    setMode2DailyPnL(mode2DailyPnL);
    // Clear other mode states to avoid confusion
    setRows([]);
    setMode3Data([]);
    setMode3ChartRows([]);
    // If no data returned for exact date range, auto-widen range once to last 60 days
    if ((!chartRows || chartRows.length === 0) && !mode2RefetchedOnceRef.current) {
      try {
        const p2 = new URLSearchParams(params.toString());
        const toStr = p2.get('to') || '';
        const end = toStr ? new Date(toStr) : new Date();
        const start = new Date(end);
        start.setDate(start.getDate() - 60);
        const y = start.getFullYear();
        const m = String(start.getMonth() + 1).padStart(2, '0');
        const d = String(start.getDate()).padStart(2, '0');
        const startStr = `${y}-${m}-${d}`;
        p2.set('from', startStr);
        p2.set('mode', '2');
        mode2RefetchedOnceRef.current = true;
        await fetchData(p2);
      } catch (err) {
        // ignore
      }
    }
    // Second fallback: if still empty after widening range, drop model filter once
    if ((!chartRows || chartRows.length === 0) && mode2RefetchedOnceRef.current && !mode2TriedNoModelRef.current) {
      try {
        const p3 = new URLSearchParams(params.toString());
        p3.delete('model');
        p3.set('mode', '2');
        mode2TriedNoModelRef.current = true;
        await fetchData(p3);
      } catch (err) {
        // ignore
      }
    }
  } else if (String(params.get('mode') || mode) === '3') {
    // Build per-model aggregates and chart rows from unified rows
    const tfSel = params.get('timeframe') || timeframe;
    const chartRows = [];
    // First, build chart rows from all data (including without actual)
    for (const r of listAll) {
      const dt = r.create_date ? new Date(r.create_date) : null;
      if (!dt || isNaN(dt.getTime())) continue;
      const y = dt.getUTCFullYear();
      const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
      const d = String(dt.getUTCDate()).padStart(2, '0');
      let dateStr = `${y}-${m}-${d}`;
      if (tfSel === '4h') {
        const h = dt.getUTCHours();
        const slot = Math.floor(h / 4) * 4;
        const hh = String(slot).padStart(2, '0');
        dateStr = `${y}-${m}-${d} ${hh}:00:00`;
      }
  const actual = (r.actual_price != null && r.actual_price !== '') ? parseFloat(r.actual_price) : parseFloat(r.current_price);
  const actualLow = (r.actual_low != null && r.actual_low !== '') ? parseFloat(r.actual_low) : null;
  const actualHigh = (r.actual_high != null && r.actual_high !== '') ? parseFloat(r.actual_high) : null;
      const low = parseFloat(r.prediction_low);
      const high = parseFloat(r.prediction_high);
      const modelName = r.model || '';
      const score = (r.performance != null && r.performance !== '') ? parseFloat(r.performance) : null;
      chartRows.push({
        date: dateStr,
        actual_price: isFinite(actual) ? actual : null,
  actual_low: (typeof actualLow === 'number' && isFinite(actualLow)) ? actualLow : null,
  actual_high: (typeof actualHigh === 'number' && isFinite(actualHigh)) ? actualHigh : null,
        prediction_low: isFinite(low) ? low : null,
        prediction_high: isFinite(high) ? high : null,
        model: modelName,
        score
      });
    }
    // Then, build aggregates from filtered data (only with actual)
  const byModel = new Map();
    for (const r of filteredList) {
      const dt = r.create_date ? new Date(r.create_date) : null;
      if (!dt || isNaN(dt.getTime())) continue;
      const y = dt.getUTCFullYear();
      const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
      const d = String(dt.getUTCDate()).padStart(2, '0');
      let dateStr = `${y}-${m}-${d}`;
      if (tfSel === '4h') {
        const h = dt.getUTCHours();
        const slot = Math.floor(h / 4) * 4;
        const hh = String(slot).padStart(2, '0');
        dateStr = `${y}-${m}-${d} ${hh}:00:00`;
      }
      const actual = (r.actual_price != null && r.actual_price !== '') ? parseFloat(r.actual_price) : parseFloat(r.current_price);
      const actualLow = (r.actual_low != null && r.actual_low !== '') ? parseFloat(r.actual_low) : null;
      const actualHigh = (r.actual_high != null && r.actual_high !== '') ? parseFloat(r.actual_high) : null;
      const low = parseFloat(r.prediction_low);
      const high = parseFloat(r.prediction_high);
      const priceAtPred = parseFloat(r.current_price || r.currentPrice || r.price_at_prediction || r.priceAtPrediction || r.price);
      const modelName = r.model || '';
      const score = (r.performance != null && r.performance !== '') ? parseFloat(r.performance) : null;
      if (!byModel.has(modelName)) byModel.set(modelName, { model: modelName, prediction_count: 0, scores: [], minDate: null, maxDate: null, inRange: 0, outRange: 0, predictionRanges: [], actualRanges: [] });
      const agg = byModel.get(modelName);
      agg.prediction_count += 1;
      if (isFinite(score)) agg.scores.push(score);
      if (!agg.minDate || dateStr < agg.minDate) agg.minDate = dateStr;
      if (!agg.maxDate || dateStr > agg.maxDate) agg.maxDate = dateStr;
      // Calculate prediction range as percentage from Price@Pred
      if (isFinite(low) && isFinite(high) && isFinite(priceAtPred) && priceAtPred !== 0) {
        const lowPct = ((low - priceAtPred) / priceAtPred) * 100;
        const highPct = ((high - priceAtPred) / priceAtPred) * 100;
        const predRange = highPct - lowPct;
        if (isFinite(predRange)) agg.predictionRanges.push(predRange);
      }
      // Calculate actual range as percentage from Price@Pred
      if (isFinite(actualLow) && isFinite(actualHigh) && isFinite(priceAtPred) && priceAtPred !== 0) {
        const actualLowPct = ((actualLow - priceAtPred) / priceAtPred) * 100;
        const actualHighPct = ((actualHigh - priceAtPred) / priceAtPred) * 100;
        const actualRange = actualHighPct - actualLowPct;
        if (isFinite(actualRange)) agg.actualRanges.push(actualRange);
      }
      const hasActual = isFinite(actual);
      const hasLow = isFinite(low);
      const hasHigh = isFinite(high);
      if (hasActual && hasLow && hasHigh && isFinite(score)) {
        if (actual >= low && actual <= high) agg.inRange += 1; else agg.outRange += 1;
      }
    }
    const data = Array.from(byModel.values()).map(it => ({
      model: it.model,
      prediction_count: it.prediction_count,
      avg_score: it.scores.length ? (it.scores.reduce((a,b)=>a+b,0) / it.scores.length) : null,
      avg_prediction_range: it.predictionRanges.length ? (it.predictionRanges.reduce((a,b)=>a+b,0) / it.predictionRanges.length) : null,
      avg_actual_range: it.actualRanges.length ? (it.actualRanges.reduce((a,b)=>a+b,0) / it.actualRanges.length) : null,
      date_range: (it.minDate && it.maxDate) ? `${formatDisplayDate(it.minDate)} to ${formatDisplayDate(it.maxDate)}` : null,
      min_date: it.minDate,
      max_date: it.maxDate,
      in_range_count: it.inRange,
      out_range_count: it.outRange
    })).sort((a,b) => {
      const as = isFinite(a.avg_score) ? a.avg_score : -Infinity;
      const bs = isFinite(b.avg_score) ? b.avg_score : -Infinity;
      return bs - as;
    });
    setMode3Data(data);
    setMode3ChartRows(chartRows);
    setRows([]);
    setMode2Summary([]);
    setMode2ChartRows([]);
    setMode2DailyPnL({});
    // If no data returned for exact date range, auto-widen range once to last 60 days
    if ((!data || data.length === 0) && !mode3RefetchedOnceRef.current) {
      try {
        const p2 = new URLSearchParams(params.toString());
        const toStr = p2.get('to') || '';
        const end = toStr ? new Date(toStr) : new Date();
        const start = new Date(end);
        start.setDate(start.getDate() - 60);
        const y = start.getFullYear();
        const m = String(start.getMonth() + 1).padStart(2, '0');
        const d = String(start.getDate()).padStart(2, '0');
        const startStr = `${y}-${m}-${d}`;
        p2.set('from', startStr);
        p2.set('mode', '3');
        mode3RefetchedOnceRef.current = true;
        await fetchData(p2);
      } catch (err) {
        // ignore
      }
    }
    // Second fallback: if still empty after widening range, drop model filter once
    if ((!data || data.length === 0) && mode3RefetchedOnceRef.current && !mode3TriedNoModelRef.current) {
      try {
        const p3 = new URLSearchParams(params.toString());
        p3.delete('model');
        p3.set('mode', '3');
        mode3TriedNoModelRef.current = true;
        await fetchData(p3);
      } catch (err) {
        // ignore
      }
    }
  } else {
    const list = Array.isArray(payload) ? payload : (payload?.data || []);
    // Sort predictions based on score availability or custom sorting
    let sortedList;
    if (sortBy) {
      sortedList = sortPredictionsByColumn(list, sortBy, sortOrder);
    } else {
      sortedList = sortPredictions(list);
    }
    setRows(sortedList);
    setMode2Summary([]);
    setMode2ChartRows([]);
    setMode2DailyPnL({});
    setMode3Data([]);
  }
      lastFetchKeyRef.current = fetchKey;
    } catch (e) {
  // Swallow errors without noisy console output
      setErrorMsg(e?.message || 'Failed to load');
      setRows([]);
      setMode2Summary([]);
      setMode2ChartRows([]);
      setMode2DailyPnL({});
  setMode3ChartRows([]);
      setMode3Data([]);
    } finally {
  pendingFetchKeyRef.current = '';
      setLoading(false);
    }
  };

  // Build query params from current state
  const buildQueryFromState = () => {
    const params = new URLSearchParams({ symbol });
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    if (timeframe) params.append('timeframe', timeframe);
  if (Array.isArray(selectedModels) && selectedModels.length) params.append('model', selectedModels.join('+'));
    // Only include hours parameter when timeframe is 4h
    if (timeframe === '4h' && selectedHours && selectedHours.length) params.append('hours', selectedHours.join('+'));
    // Include panel visibility if it's hidden (default is shown)
    if (!showOptionsPanel) params.append('hidepanel', 'true');
    // Mode 2 visibility toggles
    if (mode === 2) {
      if (!mode2ShowActualPrice) params.append('m2actual', '0');
      if (!mode2ShowActualRange) params.append('m2actualrange', '0');
    }
    // Include chart type if it's not the default
    if (chartType !== 'tradingview') params.append('chartType', chartType);
    // Include sorting parameters
    if (sortBy) params.append('sortBy', sortBy);
    if (sortOrder !== 'desc') params.append('sortOrder', sortOrder);
  // Include mode
  if (mode && mode !== 1) params.append('mode', String(mode));
    // Include performance mode if enabled
    if (isPerformanceMode) params.append('performance', 'true');
    // Focused models on charts (frontend-only)
    if (mode2FocusedModels && mode2FocusedModels.length) params.append('m2models', mode2FocusedModels.join('+'));
    if (mode3FocusedModels && mode3FocusedModels.length) params.append('m3models', mode3FocusedModels.join('+'));
    return params;
  };

  // Apply URL query params to state on load or navigation
  const applyStateFromLocation = () => {
    const search = window.location.search || '';
    const params = new URLSearchParams(search);
    const qsSymbol = params.get('symbol') || undefined;
    const qsTimeframe = params.get('timeframe') || undefined;
    const qsFrom = params.get('from') || '';
    const qsTo = params.get('to') || '';
    const qsModel = params.get('model') || '';
    const qsHours = params.get('hours') || '';
    const qsHidePanel = params.get('hidepanel') || '';
    const qsChartType = params.get('chartType') || 'tradingview';
    const qsSortBy = params.get('sortBy') || '';
    const qsSortOrder = params.get('sortOrder') || 'desc';
  const qsMode = parseInt(params.get('mode') || '1', 10);
  const qsM2Actual = params.get('m2actual'); // '0' hides, otherwise default show
  const qsM2ActualRange = params.get('m2actualrange'); // '0' hides, otherwise default show
  const qsM2Model = params.get('m2model') || '';
  const qsM2Models = params.get('m2models') || '';
  const qsM3Models = params.get('m3models') || '';

    if (qsSymbol && availableSymbols.includes(qsSymbol)) {
      setSymbol(qsSymbol);
    }
    if (typeof qsTimeframe === 'string' && timeframes.includes(qsTimeframe)) {
      setTimeframe(qsTimeframe);
    }
    setFrom(qsFrom);
    setTo(qsTo);
    if (!isNaN(qsMode) && [1,2,3].includes(qsMode)) {
      setMode(qsMode);
      // Reset last fetch key on mode change to force refetch
      lastFetchKeyRef.current = '';
  // Reset fallbacks when switching modes
  mode2RefetchedOnceRef.current = false;
  mode2TriedNoModelRef.current = false;
  mode3RefetchedOnceRef.current = false;
  mode3TriedNoModelRef.current = false;
    }
  // Mode 2 toggles from URL
  setMode2ShowActualPrice(qsM2Actual === '0' ? false : true);
  setMode2ShowActualRange(qsM2ActualRange === '0' ? false : true);
    if (qsModel) {
      const allowed = new Set([...PRIMARY_MODELS, ...BASIC_MODELS].map(m => m.value));
      const parsed = qsModel.split('+').filter(Boolean).filter(v => allowed.has(v));
      setSelectedModels(parsed);
    } else {
      setSelectedModels([]);
    }
    if (qsHours) {
      const parsed = qsHours.split('+').filter(Boolean).filter(h => hourOptions.includes(h));
      setSelectedHours(parsed);
    } else {
      setSelectedHours([]);
    }
  // Set panel visibility (default is true, hide only if explicitly set or when clean mode)
  setShowOptionsPanel(qsHidePanel !== 'true' && !isCleanMode);
    // Set chart type (default is dashboard-crypto)
    if (['tradingview', 'dashboard-crypto'].includes(qsChartType)) {
      setChartType(qsChartType);
    }
    // Set sorting parameters
    setSortBy(qsSortBy);
    if (['asc', 'desc'].includes(qsSortOrder)) {
      setSortOrder(qsSortOrder);
    }
    // Set Mode 2 focused models (supports legacy m2model and new m2models)
    {
      const allowed = new Set([...PRIMARY_MODELS, ...BASIC_MODELS].map(m => m.value));
      let arr = [];
      if (qsM2Models) {
        arr = qsM2Models.split('+').filter(Boolean).filter(v => allowed.has(v));
      } else if (qsM2Model) {
        if (allowed.has(qsM2Model)) arr = [qsM2Model];
      }
      setMode2FocusedModels(arr);
    }
    // Set Mode 3 focused models
    {
      const allowed = new Set([...PRIMARY_MODELS, ...BASIC_MODELS].map(m => m.value));
      let arr = [];
      if (qsM3Models) arr = qsM3Models.split('+').filter(Boolean).filter(v => allowed.has(v));
      setMode3FocusedModels(arr);
    }
  };

  const renderTv = async (overrideSymbol, overrideTimeframe) => {
    // Now delegated to TradingViewChart via props and onReady
    setTvReady(true);
  };

  const drawPredictionShapes = () => {
    // Note: TradingView embedded widget doesn't support drawing API
    // Triangle drawing is available in the backend HTML report (/report) which uses tv.js
    if (!rows || !rows.length) return;
    
    console.log('Prediction data loaded for chart - triangles will be visible in /report view:');
    rows.forEach((r, index) => {
      console.log(`Prediction ${index + 1}:`, {
        model: r.model,
        createDate: r.create_date,
        targetDate: r.target_date,
        priceAtPrediction: r.current_price,
        predictionLow: r.prediction_low,
        predictionHigh: r.prediction_high
      });
    });
  };

  const addVolumeStudy = () => {
    if (!tvWidgetRef.current) return;
    
    try {
      setTimeout(() => {
        const chart = tvWidgetRef.current.chart && tvWidgetRef.current.chart();
        if (chart) {
          try {
            // Try the simplest method first
            chart.createStudy('Volume');
            // volume study added
          } catch (e) {
            // basic method failed; try alternatives
            
            // Try alternative methods
            try {
              chart.createStudy('volume', false, false);
              // alternative method worked
            } catch (e2) {
              try {
                chart.createStudy('Volume@tv-basicstudies');
                // tv-basicstudies method worked
              } catch (e3) {
                // all methods failed; continue without volume
              }
            }
          }
        }
      }, 3000); // Wait longer for chart to be fully ready
    } catch (e) { 
      // ignore volume add failures
    }
  };

  const removeVolumeStudy = () => {
    // Volume should be displayed, so we'll comment out this function
    // if (!tvWidgetRef.current) return;
    // const chart = tvWidgetRef.current.chart && tvWidgetRef.current.chart();
    // if (!chart || typeof chart.getAllStudies !== 'function') return;
    // try {
    //   const studies = chart.getAllStudies();
    //   (studies || []).forEach((s) => {
    //     const name = (s && (s.name || s.fullid || '')).toString().toLowerCase();
    //     if (name.includes('volume')) {
    //       try { chart.removeEntity(s.id); } catch (e) {}
    //     }
    //   });
    // } catch (e) { /* noop */ }
  };

  const removeAllStudies = () => {
    if (!tvWidgetRef.current) return;
    const chart = tvWidgetRef.current.chart && tvWidgetRef.current.chart();
    if (!chart || typeof chart.getAllStudies !== 'function') return;
    try {
      const studies = chart.getAllStudies();
      (studies || []).forEach((s) => { 
        try { 
          // Don't remove volume studies - we want to keep them
          const name = (s && (s.name || s.fullid || '')).toString().toLowerCase();
          if (!name.includes('volume')) {
            chart.removeEntity(s.id); 
          }
        } catch (e) {} 
      });
    } catch (e) { /* noop */ }
  };

  const ensurePriceSymbol = (desiredSymbolParam, desiredIntervalParam) => {
    try {
      const w = tvWidgetRef.current;
      if (!w || typeof w.chart !== 'function') return;
      const chartObj = w.chart();
      if (!chartObj || typeof chartObj.symbol !== 'function' || typeof chartObj.setSymbol !== 'function') return;
      let attempts = 0;
      const check = () => {
        attempts += 1;
        try {
          const cur = chartObj.symbol();
          // Normalize current symbol to a string for comparison
          const curSymRaw = (typeof cur === 'string') ? cur : (cur && (cur.full_name || cur.name || cur.symbol)) || '';
          const curSym = (curSymRaw || '').toString().toUpperCase();
          const desiredSym = (desiredSymbolParam || tvSymbol || '').toString().toUpperCase();
          const desiredInt = desiredIntervalParam || tvInterval;
          const isMinuteRes = ['1','3','5'].includes(String(desiredInt));
          const isBad = curSym.includes('CRYPTOCAP') || curSym.includes('TOTAL') || curSym.includes('MARKET CAP') || curSym.includes('BTC.D') || curSym.includes('DOMINANCE') || curSym.includes('MARKETCAP');
          // If symbol contains BTC but not USD/USDT, likely not a price pair
          const looksNonPrice = curSym.includes('BTC') && !(curSym.includes('USD') || curSym.includes('USDT'));
          const isMismatch = curSym && desiredSym && curSym !== desiredSym;
          // debug logging removed
          // Re-apply interval for minute resolutions during early attempts to prevent auto-bump
          if (isBad || looksNonPrice || isMismatch || (isMinuteRes && attempts <= 8)) {
            try { chartObj.setSymbol(desiredSym, desiredInt); } catch (e) {}
          }
        } catch (e) { /* noop */ }
        if (attempts < 30) setTimeout(check, 200); // More frequent checks, more attempts
      };
      check();
    } catch (e) { /* noop */ }
  };

  useEffect(() => {
    // initial load: take state from URL if present and fetch using those params directly
    const search = window.location.search || '';
    const params = new URLSearchParams(search);
    // If URL has no search but we have a last known query, restore it
    if (!search) {
      try {
        const last = sessionStorage.getItem(LAST_SEARCH_KEY);
        if (last && typeof last === 'string' && last.trim()) {
          const url = `${window.location.pathname}?${last}`;
          window.history.replaceState({}, '', url);
        }
      } catch (e) { /* noop */ }
    } else {
      // Persist current search for resilience across accidental wipes
      try { sessionStorage.setItem(LAST_SEARCH_KEY, search.replace(/^\?/, '')); } catch (e) { /* noop */ }
    }
    applyStateFromLocation();
    
    // Initialize chart with URL parameters
    const qsSymbol = params.get('symbol');
    const qsTimeframe = params.get('timeframe');
    const qsMode = parseInt(params.get('mode') || '1', 10);
    if (qsSymbol && availableSymbols.includes(qsSymbol)) {
      setChartSymbol(qsSymbol);
    }
    if (qsTimeframe && timeframes.includes(qsTimeframe)) {
      setChartTimeframe(qsTimeframe);
    }
    if (!isNaN(qsMode) && [1,2,3].includes(qsMode)) {
      setMode(qsMode);
      lastFetchKeyRef.current = '';
      mode2RefetchedOnceRef.current = false;
    }
    
    fetchData(params);
  // Chart render handled by component below
    // eslint-disable-next-line
  }, []);

  // React to in-app navigations that change only the query string (push/replace),
  // ensuring the page refreshes filters, data, and chart accordingly.
  useEffect(() => {
  // debug logging removed
    // Save non-empty search for recovery later
    try {
      if (location.search && location.search !== '?') {
        sessionStorage.setItem(LAST_SEARCH_KEY, location.search.replace(/^\?/, ''));
      }
    } catch (e) { /* noop */ }

    // If search is unexpectedly cleared, try to restore immediately
    if (!location.search || location.search === '?') {
      try {
        const last = sessionStorage.getItem(LAST_SEARCH_KEY);
        if (last && typeof last === 'string' && last.trim()) {
          const restoredUrl = `${window.location.pathname}?${last}`;
          window.history.replaceState({}, '', restoredUrl);
        }
      } catch (e) { /* noop */ }
    }
    applyStateFromLocation();
    
    // Update chart when URL navigation happens (e.g., menu clicks)
    const search = location.search || '';
    const params = new URLSearchParams(search);
    const qsSymbol = params.get('symbol');
    const qsTimeframe = params.get('timeframe');
    if (qsSymbol && availableSymbols.includes(qsSymbol)) {
      setChartSymbol(qsSymbol);
    }
    if (qsTimeframe && timeframes.includes(qsTimeframe)) {
      setChartTimeframe(qsTimeframe);
    }
    
    // Defer to allow state setters to commit before using derived values
    // Build a fetch key without the hidepanel param to compare if only panel toggled
    const fetchKey = buildFetchKey(params);
    // If the only change was hidepanel, fetchKey will be identical to lastFetchKeyRef
    setTimeout(() => {
  if (!fetchKey || fetchKey !== lastFetchKeyRef.current) {
        fetchData(params);
      }
    }, 0);
  }, [location.search]);

  // Chart rerender is handled by props changes to TradingViewChart

  const onApply = async (e) => {
    e.preventDefault();
    // Update chart parameters to match current form state
    setChartSymbol(symbol);
    setChartTimeframe(timeframe);
    // update URL with current filters
    const params = buildQueryFromState();
    const url = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, '', url);
  mode2RefetchedOnceRef.current = false;
  mode2TriedNoModelRef.current = false;
  mode3RefetchedOnceRef.current = false;
  mode3TriedNoModelRef.current = false;
    await fetchData();
  };

  // Keep URL in sync with focused models for Mode 2 and Mode 3 (no data refetch)
  useEffect(() => {
    try {
      const search = window.location.search || '';
      const params = new URLSearchParams(search);
      const prevM2 = params.get('m2models') || '';
      const prevM3 = params.get('m3models') || '';
      let changed = false;

      // Mode 2 selection
      if (Array.isArray(mode2FocusedModels) && mode2FocusedModels.length > 0) {
        const next = mode2FocusedModels.join('+');
        if (prevM2 !== next) { params.set('m2models', next); changed = true; }
      } else if (prevM2) { params.delete('m2models'); changed = true; }

      // Mode 3 selection
      if (Array.isArray(mode3FocusedModels) && mode3FocusedModels.length > 0) {
        const next = mode3FocusedModels.join('+');
        if (prevM3 !== next) { params.set('m3models', next); changed = true; }
      } else if (prevM3) { params.delete('m3models'); changed = true; }

      if (changed) {
        const newSearch = params.toString();
        const url = newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname;
        window.history.replaceState({}, '', url);
      }
    } catch (e) { /* noop */ }
  }, [mode2FocusedModels, mode3FocusedModels]);

  // Handle back/forward navigation to keep filters in sync with URL
  useEffect(() => {
    const onPop = () => {
  const search = window.location.search || '';
  const params = new URLSearchParams(search);
  applyStateFromLocation();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line
  }, []);

  // Whenever table rows update and chart is ready, (re)draw shapes
  useEffect(() => {
    if (!tvWidgetRef.current) return;
    drawPredictionShapes();
    // eslint-disable-next-line
  }, [rows]);

  // No dynamic model loading; using fixed groups above
  // Derived chart series/options for Mode 2
  const mode2Series = useMemo(() => {
    if (!Array.isArray(mode2ChartRows) || mode2ChartRows.length === 0) return [];
    
    // Group by date and model for model-specific ranges
    const byDateModel = new Map();
    mode2ChartRows.forEach(r => {
      const d = r.date;
      const model = r.model || 'Unknown';
      const ap = parseFloat(r.actual_price);
      const low = parseFloat(r.prediction_low);
      const high = parseFloat(r.prediction_high);
      if (!d) return;
      
      const key = `${d}_${model}`;
      const entry = byDateModel.get(key) || { date: d, model, prices: [], lows: [], highs: [] };
      if (!isNaN(ap)) entry.prices.push(ap);
      if (!isNaN(low)) entry.lows.push(low);
      if (!isNaN(high)) entry.highs.push(high);
      byDateModel.set(key, entry);
    });
    
    // Group by date for actual prices (single line) and actual ranges (low/high)
    const pricesByDate = new Map();
    const actualRangeByDate = new Map();
    mode2ChartRows.forEach(r => {
      const d = r.date;
      const ap = parseFloat(r.actual_price);
      const al = parseFloat(r.actual_low);
      const ah = parseFloat(r.actual_high);
      if (!d || isNaN(ap)) return;
      
      const entry = pricesByDate.get(d) || { date: d, prices: [] };
      entry.prices.push(ap);
      pricesByDate.set(d, entry);

      // Track min/max of actual low/high per date to form a band
      const rng = actualRangeByDate.get(d) || { date: d, lows: [], highs: [] };
      if (!isNaN(al)) rng.lows.push(al);
      if (!isNaN(ah)) rng.highs.push(ah);
      actualRangeByDate.set(d, rng);
    });
    
    // Build actual price series (bright white for visibility)
    const sortedPrices = Array.from(pricesByDate.values()).sort((a, b) => (a.date > b.date ? 1 : -1));
    const priceSeries = sortedPrices
      .map(e => {
        const ds = e.date.includes(':') ? `${e.date}Z` : `${e.date}T00:00:00Z`;
        const x = Date.parse(ds);
        const y = e.prices.length ? e.prices[e.prices.length - 1] : null;
        return { x, y };
      })
      .filter(p => typeof p.y === 'number' && isFinite(p.y) && p.y >= 0);
    
  // Build model-specific prediction ranges (group all dates per model)
    const modelRanges = new Map(); // model -> [{x, y:[low,high]}]
    byDateModel.forEach((data) => {
      const modelName = data.model || 'Unknown';
  const lows = data.lows.filter(n => typeof n === 'number' && isFinite(n) && n >= 0);
  const highs = data.highs.filter(n => typeof n === 'number' && isFinite(n) && n >= 0);
      if (!lows.length || !highs.length) return;
      const lo = Math.min(...lows);
      const hi = Math.max(...highs);
  if (!isFinite(lo) || !isFinite(hi) || lo > hi) return;
      const ds = data.date.includes(':') ? `${data.date}Z` : `${data.date}T00:00:00Z`;
      const x = Date.parse(ds);
      if (!isFinite(x)) return;
      if (!modelRanges.has(modelName)) modelRanges.set(modelName, []);
      modelRanges.get(modelName).push({ x, y: [lo, hi] });
    });
    
  let series = [];
    
    // Add model-specific prediction ranges
  const focusSet = new Set((mode2FocusedModels || []).map(v => v));
    Array.from(modelRanges.entries()).forEach(([modelName, data]) => {
      if (data.length === 0) return;
      if (focusSet.size > 0 && !focusSet.has(toCanonicalModelValue(modelName))) return;
      series.push({
        name: `${modelName} Range`,
        type: 'rangeArea',
        data: data.sort((a, b) => a.x - b.x),
        color: getModelColor(modelName)
      });
    });

    // Diagnostic: log series count vs models
    if (series.length > 0) {
      const modelSeriesCount = series.filter(s => s.name !== 'Actual Price').length;
      console.log(`Mode2 series built: ${modelSeriesCount} model ranges + Actual Price (was ${byDateModel.size} date-model entries)`);
    }
    
    // Add actual low/high band as dashed gray rangeArea
    const sortedActualRanges = Array.from(actualRangeByDate.values()).sort((a, b) => (a.date > b.date ? 1 : -1));
    const actualBand = sortedActualRanges
      .map(e => {
        const ds = e.date.includes(':') ? `${e.date}Z` : `${e.date}T00:00:00Z`;
        const x = Date.parse(ds);
        const lows = (e.lows || []).filter(n => typeof n === 'number' && isFinite(n) && n >= 0);
        const highs = (e.highs || []).filter(n => typeof n === 'number' && isFinite(n) && n >= 0);
        const lo = lows.length ? Math.min(...lows) : null;
        const hi = highs.length ? Math.max(...highs) : null;
        return { x, y: (isFinite(lo) && isFinite(hi) && lo <= hi) ? [lo, hi] : null };
      })
      .filter(p => Array.isArray(p.y));
  if (actualBand.length > 0 && mode2ShowActualRange) {
      series.push({
        name: 'Actual Range',
        type: 'rangeArea',
        data: actualBand,
        color: '#9e9e9e'
      });
    }

    // Add actual price series (as a rangeArea with same start/end)
  if (priceSeries.length > 0 && mode2ShowActualPrice) {
      series.push({
        name: 'Actual Price',
        type: 'rangeArea',
        data: priceSeries.map(p => ({ x: p.x, y: [p.y, p.y] })),
        color: '#000000'
      });
    }
    
    // Add cumulative P/L series if enabled
    if (mode2ShowCumulativePnL && mode2DailyPnL && Object.keys(mode2DailyPnL).length > 0) {
      const focusSet = new Set((mode2FocusedModels || []).map(v => v));
      Object.entries(mode2DailyPnL).forEach(([modelName, dailyData]) => {
        if (focusSet.size > 0 && !focusSet.has(toCanonicalModelValue(modelName))) return;
        if (Array.isArray(dailyData) && dailyData.length > 0) {
          const pnlData = dailyData.map(d => {
            const ds = d.date && d.date.includes(':') ? `${d.date}Z` : `${d.date || ''}T00:00:00Z`;
            const x = Date.parse(ds);
            const y = (d.cumulativePnL || 0) * 100; // Convert to percentage
            return { x, y };
          }).filter(p => isFinite(p.x) && isFinite(p.y));
          
          if (pnlData.length > 0) {
            series.push({
              name: `${modelName} Cumulative P/L`,
              type: 'line',
              data: pnlData.sort((a, b) => a.x - b.x),
              color: getModelColor(modelName),
              stroke: { width: 2 }
            });
          }
        }
      });
    }
    
    return series;
  }, [mode2ChartRows, mode2FocusedModels, mode2ShowActualPrice, mode2ShowActualRange, mode2ShowCumulativePnL, mode2DailyPnL]);

  const mode2Options = useMemo(() => {
    // Compute dynamic y-axis bounds based on visible series
    const extractYValues = (series) => {
      const ys = [];
      (series || []).forEach(s => {
        (s.data || []).forEach(p => {
          const y = p && p.y;
          if (Array.isArray(y)) {
            if (isFinite(y[0])) ys.push(y[0]);
            if (isFinite(y[1])) ys.push(y[1]);
          } else if (isFinite(y)) {
            ys.push(y);
          }
        });
      });
      return ys;
    };
  const ys = extractYValues(mode2Series).filter(v => typeof v === 'number' && isFinite(v) && v >= 0);
    let yMin, yMax;
    if (ys.length) {
      const rawMin = Math.min(...ys);
      const rawMax = Math.max(...ys);
      // Round bounds outward using two significant non-zero digits.
      const roundDown2Sig = (v) => {
        if (!isFinite(v) || v <= 0) return 0;
        const e = Math.floor(Math.log10(v)) - 1; // keep two significant digits
        const f = Math.pow(10, e);
        return Math.floor(v / f) * f;
      };
      const roundUp2Sig = (v) => {
        if (!isFinite(v) || v <= 0) return 0;
        const e = Math.floor(Math.log10(v)) - 1;
        const f = Math.pow(10, e);
        return Math.ceil(v / f) * f;
      };
      yMin = roundDown2Sig(rawMin);
      yMax = roundUp2Sig(rawMax);
      // If bounds collapse (very tight range), expand by one step on each side
      if (!(isFinite(yMin) && isFinite(yMax)) || yMin === yMax) {
        const ref = rawMax || 1;
        const e = Math.floor(Math.log10(ref)) - 1;
        const step = Math.max(1, Math.pow(10, e));
        yMin = Math.max(0, (isFinite(yMin) ? yMin : rawMin) - step);
        yMax = (isFinite(yMax) ? yMax : rawMax) + step;
      }
    }

    // Dynamic stroke widths: model ranges thin, actual band medium (dashed), actual price thick
    const strokeWidths = (mode2Series || []).map((s) => {
      if (s.name === 'Actual Price') return 5;
      if (s.name === 'Actual Range') return 2;
      return 2;
    });

    const focusLabel = (mode2FocusedModels && mode2FocusedModels.length)
      ? (() => {
          const labels = mode2FocusedModels.map(v => VALUE_TO_LABEL[v] || v);
          return labels.length <= 3 ? labels.join(', ') : `${labels.slice(0,2).join(', ')} and ${labels.length - 2} others`;
        })()
      : 'All Models';

    return ({
    chart: { 
      type: 'rangeArea', 
      height: 300, 
      foreColor: '#adb5bd', 
      toolbar: { show: true },
      background: 'transparent',
      events: {
        legendClick: function(chartContext, seriesIndex, config) {
          try {
            const name = (config?.globals?.seriesNames?.[seriesIndex] || '').toLowerCase();
            if (name === 'actual price') {
              setMode2ShowActualPrice((v) => !v);
            } else if (name === 'actual range') {
              setMode2ShowActualRange((v) => !v);
            }
          } catch (e) { /* noop */ }
        }
      }
    },
    title: {
      text: `Prediction vs Actual — ${symbol} (${timeframe || 'All'}) • ${focusLabel}`,
      align: 'left',
      style: { color: '#adb5bd' }
    },
    dataLabels: { enabled: false },
    markers: { size: 0 },
    stroke: { 
      curve: 'smooth', 
      width: strokeWidths,
      dashArray: (mode2Series || []).map((s) => (s.name === 'Actual Range' ? 6 : 0))
    },
  // Colors are provided per-series above for stability; keep fallback palette aligned to families
  // Base palette fallback; actual series colors are set directly
  colors: ['#43a047', '#f4b400', '#1e88e5', '#81c784', '#ffd54f', '#ffe082', '#64b5f6', '#9e9e9e', '#000000'],
    fill: { 
      type: 'gradient',
      gradient: { 
        shadeIntensity: 0.3, 
        opacityFrom: 0.2, 
        opacityTo: 0.1 
      } 
    },
    xaxis: { 
      type: 'datetime', 
      labels: { rotate: -15 } 
    },
  yaxis: { 
      labels: { 
        formatter: (v) => (typeof v === 'number' && !isNaN(v) ? v.toFixed(2) : '') 
      },
      min: typeof yMin === 'number' ? yMin : undefined,
      max: typeof yMax === 'number' ? yMax : undefined
    },
    tooltip: { shared: true },
    legend: { position: 'top', show: true }
  });
  }, [mode2Series, symbol, timeframe, mode2FocusedModels, mode2ShowActualPrice, mode2ShowActualRange]);

  // Sync URL with Mode 2 visibility toggles so menu reflects legend clicks
  useEffect(() => {
    if (mode !== 2) return;
    try {
      const params = buildQueryFromState();
      const newSearch = params.toString();
      const url = newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname;
      window.history.replaceState({}, '', url);
    } catch (e) { /* noop */ }
    // eslint-disable-next-line
  }, [mode2ShowActualPrice, mode2ShowActualRange]);

  // Calculate backtest results based on strategy and predictions
  const calculateBacktestResults = (predictions, strategy) => {
    if (!Array.isArray(predictions) || predictions.length === 0) {
      return null;
    }

    const results = {
      totalTrades: 0,
      winningTrades: 0,
      losingTrades: 0,
      winRate: 0,
      totalReturn: 0,
      cumulativeReturns: [],
      tradeDetails: []
    };

    let cumulativeReturn = 1;
    const trades = [];

    // Calculate signal for each prediction based on strategy
    predictions.forEach((pred) => {
      const signal = (pred.signal || '').toUpperCase();
      const isBullish = signal.includes('BULLISH');
      const isBearish = signal.includes('BEARISH');
      
      let tradeSignal = null;

      if (strategy === 'long-consensus') {
        // Only long on bullish signals
        if (isBullish) tradeSignal = 'LONG';
      } else if (strategy === 'long-short-consensus') {
        // Long on bullish, short on bearish
        if (isBullish) tradeSignal = 'LONG';
        else if (isBearish) tradeSignal = 'SHORT';
      } else if (strategy === 'long-any-bullish') {
        // Long on any bullish
        if (isBullish) tradeSignal = 'LONG';
      } else if (strategy === 'long-short-majority') {
        // Majority-based signal (assuming we have confidence/strength)
        if (isBullish) tradeSignal = 'LONG';
        else if (isBearish) tradeSignal = 'SHORT';
      }

      if (tradeSignal) {
        const entry = parseFloat(pred.current_price ?? pred.currentPrice ?? pred.price_at_prediction ?? 0);
        const actual = parseFloat(pred.actual_price ?? 0);
        
        if (entry > 0 && actual > 0) {
          const return_ = tradeSignal === 'LONG' 
            ? (actual - entry) / entry 
            : (entry - actual) / entry;
          
          cumulativeReturn *= (1 + return_);
          
          const trade = {
            date: pred.create_date || pred.date,
            signal: tradeSignal,
            entry,
            actual,
            return: return_,
            cumulativeReturn,
            isWin: return_ > 0
          };
          
          trades.push(trade);
          results.totalTrades++;
          if (trade.isWin) results.winningTrades++;
          else results.losingTrades++;
          results.cumulativeReturns.push({ 
            date: trade.date, 
            value: (cumulativeReturn - 1) * 100 
          });
        }
      }
    });

    results.totalReturn = (cumulativeReturn - 1) * 100;
    results.winRate = results.totalTrades > 0 
      ? ((results.winningTrades / results.totalTrades) * 100).toFixed(2)
      : 0;
    results.avgReturnPerTrade = results.totalTrades > 0
      ? results.totalReturn / results.totalTrades
      : 0;
    results.tradeDetails = trades;

    return results;
  };

  // Handle manual backtest calculation
  const handlePerformBacktest = () => {
    if (mode === 2 && mode2ChartRows.length > 0) {
      const results = calculateBacktestResults(mode2ChartRows, backtestStrategy);
      setBacktestResults(results);
      setBacktestPerformed(true);
    }
  };

  // Reset backtest when mode changes
  useEffect(() => {
    if (mode !== 2) {
      setBacktestResults(null);
      setBacktestPerformed(false);
    }
  }, [mode]);

  // Mode 3 chart config - Show prediction scores by model over time
  const mode3Series = useMemo(() => {
    if (!Array.isArray(mode3ChartRows) || mode3ChartRows.length === 0) return [];
    
    // Group by model and date for score tracking
    const modelScores = new Map();
    mode3ChartRows.forEach(r => {
      const model = r.model || 'Unknown';
      const d = r.date || r.bucket;
      const score = parseFloat(r.avg_score || r.score);
      
      if (!d || isNaN(score)) return;
      
      if (!modelScores.has(model)) {
        modelScores.set(model, []);
      }
      
      const ds = d.includes(':') ? `${d}Z` : `${d}T00:00:00Z`;
      const x = Date.parse(ds);
      if (isFinite(x)) {
        modelScores.get(model).push({ x, y: score });
      }
    });
    
    // Build series for each model; apply focus filter if set
    const focusSet = new Set((mode3FocusedModels || []).map(v => v));
    let series = [];
  Array.from(modelScores.entries()).forEach(([model, data]) => {
      if (focusSet.size > 0 && !focusSet.has(toCanonicalModelValue(model))) return;
      if (data.length > 0) {
        series.push({
          name: `${model} Score`,
          type: 'line',
      data: data.sort((a, b) => a.x - b.x),
      color: getModelColor(model)
        });
      }
    });
    
    return series;
  }, [mode3ChartRows, mode3FocusedModels]);

  const mode3Options = useMemo(() => {
    const focusLabel = (mode3FocusedModels && mode3FocusedModels.length)
      ? (() => {
          const labels = mode3FocusedModels.map(v => VALUE_TO_LABEL[v] || v);
          return labels.length <= 3 ? labels.join(', ') : `${labels.slice(0,2).join(', ')} and ${labels.length - 2} others`;
        })()
      : 'All Models';

    return ({
      chart: { 
        type: 'line', 
        height: 300, 
        foreColor: '#adb5bd', 
        toolbar: { show: true },
        background: 'transparent'
      },
      title: {
        text: `Prediction Score — ${symbol} (${timeframe || 'All'}) • ${focusLabel}`,
        align: 'left',
        style: { color: '#adb5bd' }
      },
      dataLabels: { enabled: false },
      markers: { size: 0 },
      stroke: { 
        curve: 'smooth', 
        width: 3
      },
      // Per-series colors are assigned directly on series; keep a fallback palette aligned to families
      // Order: Claude primary, Gemini primary, GPT primary, Claude basic, Gemini basic 1, Gemini basic 2, GPT basic
      colors: ['#43a047', '#f4b400', '#1e88e5', '#81c784', '#ffd54f', '#ffe082', '#64b5f6'],
      fill: { 
        type: 'solid'
      },
      xaxis: { 
        type: 'datetime', 
        labels: { rotate: -15 } 
      },
      yaxis: { 
        title: { text: 'Prediction Score', style: { color: '#adb5bd' } },
        min: 0,
        max: 100,
        labels: { 
          formatter: (v) => (typeof v === 'number' && !isNaN(v) ? Math.round(v).toString() : '') 
        } 
      },
  tooltip: { 
        shared: true,
        y: {
          formatter: (val) => `Score: ${Math.round(val)}`
        }
      },
  legend: { position: 'top', show: true, showForSingleSeries: true }
    });
  }, [mode3FocusedModels, symbol, timeframe]);

  // Selection helpers shared by Mode 2 and Mode 3 tables (empty focus => all selected)
  const mode2AllCanonModels = useMemo(() => (mode2Summary || []).map(r => toCanonicalModelValue(r.model)).filter(Boolean), [mode2Summary]);
  const mode3AllCanonModels = useMemo(() => (mode3Data || []).map(r => toCanonicalModelValue(r.model)).filter(Boolean), [mode3Data]);
  const isMode2Selected = (model) => {
    const canon = toCanonicalModelValue(model);
    if (!canon) return false;
    if (!Array.isArray(mode2FocusedModels) || mode2FocusedModels.length === 0) return true;
    return mode2FocusedModels.includes(canon);
  };
  const isMode3Selected = (model) => {
    const canon = toCanonicalModelValue(model);
    if (!canon) return false;
    if (!Array.isArray(mode3FocusedModels) || mode3FocusedModels.length === 0) return true;
    return mode3FocusedModels.includes(canon);
  };
  const toggleMode2Row = (model) => {
    const canon = toCanonicalModelValue(model);
    if (!canon) return;
    const present = new Set(mode2AllCanonModels);
    const curSet = new Set((mode2FocusedModels && mode2FocusedModels.length) ? mode2FocusedModels : mode2AllCanonModels);
    if (curSet.has(canon)) curSet.delete(canon); else curSet.add(canon);
    const next = Array.from(curSet);
    setMode2FocusedModels(next.length === present.size ? [] : next);
  };
  const toggleMode3Row = (model) => {
    const canon = toCanonicalModelValue(model);
    if (!canon) return;
    const present = new Set(mode3AllCanonModels);
    const curSet = new Set((mode3FocusedModels && mode3FocusedModels.length) ? mode3FocusedModels : mode3AllCanonModels);
    if (curSet.has(canon)) curSet.delete(canon); else curSet.add(canon);
    const next = Array.from(curSet);
    setMode3FocusedModels(next.length === present.size ? [] : next);
  };

  const toggleModel = (name) => {
    setSelectedModels(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  };

  const toggleHour = (hourValue) => {
    setSelectedHours(prev => {
      if (prev.includes(hourValue)) {
        return prev.filter(h => h !== hourValue);
      } else {
        return [...prev, hourValue];
      }
    });
  };

  // Toggle options panel visibility and update URL
  const toggleOptionsPanel = (existingSearch) => {
    const newShowPanel = !showOptionsPanel;
    setShowOptionsPanel(newShowPanel);

    // Update URL by preserving all existing params and only toggling hidepanel
    let baseSearch = typeof existingSearch === 'string' ? existingSearch : (window.location.search || '');
    if (!baseSearch) {
      try {
        const last = sessionStorage.getItem(LAST_SEARCH_KEY);
        if (last && typeof last === 'string') baseSearch = `?${last}`;
      } catch (e) { /* noop */ }
    }
    const params = new URLSearchParams(baseSearch);
    if (!newShowPanel) {
      params.set('hidepanel', 'true');
    } else {
      params.delete('hidepanel');
    }
    const search = params.toString();
    const url = search ? `${window.location.pathname}?${search}` : window.location.pathname;
    window.history.replaceState({}, '', url);
  };

  // Listen for menu-driven toggle requests and let this page own URL/state updates
  useEffect(() => {
    const onMenuToggle = (ev) => {
      try { 
        const detailSearch = ev && ev.detail && typeof ev.detail.search === 'string' ? ev.detail.search : undefined;
        toggleOptionsPanel(detailSearch);
      } catch (e) { /* noop */ }
    };
    window.addEventListener('report:toggle-panel', onMenuToggle);
    return () => window.removeEventListener('report:toggle-panel', onMenuToggle);
  }, [showOptionsPanel]);

  // Helper function to format prediction date based on timeframe
  // Function to get table row color class based on model
  const getModelRowClass = (model) => {
    if (!model) return '';
    
    const modelLower = model.toLowerCase();
    
    // Premium models get distinct colors
    if (modelLower.includes('claude-premium')) return 'claude-primary'; // Bright Claude color
    if (modelLower.includes('gemini-premium')) return 'gemini-primary'; // Bright Gemini color
    if (modelLower.includes('gpt-premium')) return 'gpt-primary'; // Bright GPT color
    
    // Basic models get lighter colors
    if (modelLower.includes('claude') && !modelLower.includes('premium')) return 'claude-basic'; // Light Claude color
    if (modelLower.includes('gemini') && !modelLower.includes('premium')) return 'gemini-basic'; // Light Gemini color
    if (modelLower.includes('gpt') && !modelLower.includes('premium')) return 'gpt-basic'; // Light GPT color
    
    // Default for any other models
    return '';
  };

  const formatPredictionDate = () => {
    if (!timeframe) return 'All Timeframes';
    
    // Use the 'from' date if available, otherwise fall back to current date
    const baseDate = from ? new Date(from) : new Date();
    let targetDate = new Date(baseDate);
    
    // Calculate target date based on timeframe
    switch(timeframe) {
      case '4h':
        targetDate.setHours(targetDate.getHours() + 4);
        break;
      case '1d':
        targetDate.setDate(targetDate.getDate() + 1);
        break;
      case '2d':
        targetDate.setDate(targetDate.getDate() + 2);
        break;
      case '7d':
        targetDate.setDate(targetDate.getDate() + 7);
        break;
      case '30d':
        targetDate.setDate(targetDate.getDate() + 30);
        break;
      case '180d':
        targetDate.setDate(targetDate.getDate() + 180);
        break;
      default:
        return 'All Timeframes';
    }
    
    // Format date as "Aug 16, 2025" or "Aug 23" etc.
    const now = new Date();
    const options = { 
      month: 'short', 
      day: 'numeric',
      year: targetDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    };
    
    return targetDate.toLocaleDateString('en-US', options);
  };

  // Helper function to format selected models for subtitle
  const formatSelectedModels = () => {
    if (selectedModels.length === 0) return 'All Models';
    
    const allModels = [...PRIMARY_MODELS, ...BASIC_MODELS];
    const modelNames = selectedModels.map(value => {
      const model = allModels.find(m => m.value === value);
      return model ? model.label : value;
    });
    
    if (modelNames.length <= 3) {
      return modelNames.join(', ');
    } else {
      return `${modelNames.slice(0, 2).join(', ')} and ${modelNames.length - 2} others`;
    }
  };

  // Helper function to format creation time
  const formatCreationTime = () => {
    // Use UTC timezone so the subtitle always shows UTC-based timestamp
    const now = new Date();
    const utcFormatted = now.toLocaleString('en-US', {
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    // Explicitly indicate UTC so it's clear to the viewer
    return `${utcFormatted} UTC`;
  };

  // Handle column header click for sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to descending
      setSortBy(column);
      setSortOrder('desc');
    }
    
    // Update URL
    const params = buildQueryFromState();
    params.set('sortBy', column);
    params.set('sortOrder', sortBy === column ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'desc');
    window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
    
    // Re-sort existing data immediately
    if (rows.length > 0) {
      const newOrder = sortBy === column ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'desc';
      const sorted = sortPredictionsByColumn(rows, column, newOrder);
      setRows(sorted);
    }
  };

  // Handle performance mode toggle
  const handlePerformanceModeToggle = (event) => {
    const newMode = event.target.checked;
    setIsPerformanceMode(newMode);
    
    // Build params with the new performance mode value
    const params = new URLSearchParams({ symbol });
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    if (timeframe) params.append('timeframe', timeframe);
    if (Array.isArray(selectedModels) && selectedModels.length) params.append('model', selectedModels.join('+'));
    if (timeframe === '4h' && selectedHours && selectedHours.length) params.append('hours', selectedHours.join('+'));
    if (!showOptionsPanel) params.append('hidepanel', 'true');
    if (mode === 2) {
      if (!mode2ShowActualPrice) params.append('m2actual', '0');
      if (!mode2ShowActualRange) params.append('m2actualrange', '0');
    }
    if (chartType !== 'tradingview') params.append('chartType', chartType);
    if (sortBy) params.append('sortBy', sortBy);
    if (sortOrder !== 'desc') params.append('sortOrder', sortOrder);
    if (mode && mode !== 1) params.append('mode', String(mode));
    if (newMode) params.append('performance', 'true');
    if (mode2FocusedModels && mode2FocusedModels.length) params.append('m2models', mode2FocusedModels.join('+'));
    if (mode3FocusedModels && mode3FocusedModels.length) params.append('m3models', mode3FocusedModels.join('+'));
    
    window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
  };

  // Get sort indicator for column headers
  const getSortIndicator = (column) => {
    if (sortBy !== column) return '';
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  // Handle row click to navigate to prediction detail page in new tab
  const handleRowClick = (predictionId) => {
    const baseUrl = `/prediction/${predictionId}`;
    const url = isCleanMode ? `${baseUrl}?clean=true` : baseUrl;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Helper function to format price with dynamic precision:
  // - If price > 1000, show 2 decimals
  // - Otherwise, show 4 decimals
  const formatPrice = (price) => {
    if (price === null || price === undefined || price === '') return '';
    const numPrice = Number(price);
    if (Number.isNaN(numPrice)) return price;
    const decimals = numPrice > 1000 ? 2 : 4;
    return numPrice.toFixed(decimals);
  };

  // Helper function to format confidence to whole number
  const formatConfidence = (confidence) => {
    if (confidence === null || confidence === undefined || confidence === '') return '';
    const numConfidence = parseFloat(confidence);
    return isNaN(numConfidence) ? confidence : Math.round(numConfidence);
  };

  // Helper function to format performance to whole number (no decimals)
  const formatPerformance = (performance) => {
    if (performance === null || performance === undefined || performance === '') return '';
    const numPerformance = parseFloat(performance);
    return isNaN(numPerformance) ? performance : Math.round(numPerformance);
  };

  // Compute prediction change percent range relative to price at prediction
  // Returns a string like "(-0.8 to 0.9)" or '' when insufficient data
  const formatPredChangeRange = (row) => {
    try {
      const cp = parseFloat(row.current_price ?? row.currentPrice ?? row.price_at_prediction ?? row.priceAtPrediction ?? row.price);
      const lo = parseFloat(row.prediction_low);
      const hi = parseFloat(row.prediction_high);
      if (!isFinite(cp) || cp === 0 || !isFinite(lo) || !isFinite(hi)) return '';
      // Normalize bounds (if inverted)
      const low = Math.min(lo, hi);
      const high = Math.max(lo, hi);
      const lowPct = ((low - cp) / cp) * 100;
      const highPct = ((high - cp) / cp) * 100;
      const fmt = (v) => {
        if (!isFinite(v)) return '';
        const s = v.toFixed(1);
        return s; // no % symbol and no leading + for positives per requirement example
      };
      return `(${fmt(lowPct)} to ${fmt(highPct)})`;
    } catch {
      return '';
    }
  };

  const formatATRPercent = (row) => {
    try {
      // ATR percentage is stored in ta_4hourly_json as atr.percentageRaw or calculated
      if (!row.ta_4hourly_json) return '';
      
      let taData;
      if (typeof row.ta_4hourly_json === 'string') {
        taData = JSON.parse(row.ta_4hourly_json);
      } else {
        taData = row.ta_4hourly_json;
      }
      
      if (!taData || !taData.atr) return '';
      
      // Get ATR percentage - try percentageRaw first, then percentage
      const atrPct = taData.atr.percentageRaw ?? (typeof taData.atr.percentage === 'string' ? parseFloat(taData.atr.percentage) : taData.atr.percentage);
      
      if (!isFinite(atrPct)) return '';
      
      return atrPct.toFixed(1);
    } catch {
      return '';
    }
  };

  // Compute actual change percent range (actual_low/high vs price at prediction)
  // Returns a string like "(-0.2, 0.8)" or '' when insufficient data
  const formatActualChangeRange = (row) => {
    try {
      const cp = parseFloat(row.current_price ?? row.currentPrice ?? row.price_at_prediction ?? row.priceAtPrediction ?? row.price);
      const al = parseFloat(row.actual_low);
      const ah = parseFloat(row.actual_high);
      if (!isFinite(cp) || cp === 0 || !isFinite(al) || !isFinite(ah)) return '';
      const low = Math.min(al, ah);
      const high = Math.max(al, ah);
      const lowPct = ((low - cp) / cp) * 100;
      const highPct = ((high - cp) / cp) * 100;
      const fmt = (v) => (isFinite(v) ? v.toFixed(1) : '');
      return `(${fmt(lowPct)}, ${fmt(highPct)})`;
    } catch {
      return '';
    }
  };

  // Calculate suggestion (LONG/SHORT/HOLD) based on prediction midpoint vs current price
  // Threshold: 0.3% for 1h predictions, 0.5% for all other timeframes.
  const getSuggestion = (row) => {
    try {
      const cp = parseFloat(row.current_price ?? row.currentPrice ?? row.price_at_prediction ?? row.priceAtPrediction ?? row.price);
      let lo = parseFloat(row.prediction_low);
      let hi = parseFloat(row.prediction_high);
      if (!isFinite(cp) || !isFinite(lo) || !isFinite(hi) || cp <= 0) return '';
      // Normalize bounds in case they're inverted
      if (lo > hi) { const t = lo; lo = hi; hi = t; }
      const mid = (lo + hi) / 2;
      const deviation = (mid - cp) / cp; // positive if midpoint above price
      const threshold = (row.timeframe === '1h') ? 0.003 : 0.005;
      if (deviation > threshold) {
        return 'LONG';
      } else if (deviation < -threshold) {
        return 'SHORT';
      }
      return 'HOLD';
    } catch {
      return '';
    }
  };

  // Derive a fallback signal when DB signal is missing
  // Rule: if midpoint of [low, high] deviates from current price by >= threshold,
  // then BULLISH if midpoint > price, BEARISH if midpoint < price; otherwise NEUTRAL.
  // Threshold: 0.3% for 1h, 0.5% for all other timeframes.
  const getFallbackSignal = (row) => {
    try {
      const cp = parseFloat(row.current_price ?? row.currentPrice ?? row.price_at_prediction ?? row.priceAtPrediction ?? row.price);
      let lo = parseFloat(row.prediction_low);
      let hi = parseFloat(row.prediction_high);
      if (!isFinite(cp) || !isFinite(lo) || !isFinite(hi) || cp <= 0) return '';
      // Normalize bounds in case they're inverted
      if (lo > hi) { const t = lo; lo = hi; hi = t; }
      const mid = (lo + hi) / 2;
      const deviation = (mid - cp) / cp; // positive if midpoint above price
      const threshold = (row.timeframe === '1h') ? 0.003 : 0.005;
      if (Math.abs(deviation) >= threshold) {
        return deviation > 0 ? 'BULLISH' : 'BEARISH';
      }
      return 'NEUTRAL';
    } catch {
      return '';
    }
  };

  // Calculate suggestion based on prediction logic
  // Threshold: 0.3% for 1h predictions, 0.5% for all other timeframes.
  const calculatePLBasedSuggestion = (row) => {
    try {
      const cp = parseFloat(row.current_price ?? row.currentPrice ?? row.price_at_prediction ?? row.priceAtPrediction ?? row.price);
      let lo = parseFloat(row.prediction_low);
      let hi = parseFloat(row.prediction_high);
      
      if (!isFinite(cp) || !isFinite(lo) || !isFinite(hi) || cp <= 0) {
        return 'N/A';
      }
      
      // Normalize bounds in case they're inverted
      if (lo > hi) { const t = lo; lo = hi; hi = t; }
      
      // Calculate: ((range_max + range_min) / 2 - current_price) / current_price
      const midpoint = (hi + lo) / 2;
      const deviation = (midpoint - cp) / cp;
      
      // Use 0.3% threshold for 1h, 0.5% threshold for all other timeframes
      const threshold = (row.timeframe === '1h') ? 0.003 : 0.005;
      
      if (deviation > threshold) {
        return 'LONG';
      } else if (deviation < -threshold) {
        return 'SHORT';
      } else {
        return 'CASH';
      }
    } catch {
      return 'N/A';
    }
  };

  // Returns 'Win (+1.4%)' / 'Loss (-0.8%)' for a prediction row, respecting 0.3%/0.5% thresholds.
  const getPredictionStatus = (row) => {
    try {
      const cp = parseFloat(row.current_price ?? row.currentPrice ?? row.price_at_prediction ?? row.priceAtPrediction ?? row.price);
      const ap = parseFloat(row.actual_price);
      if (!isFinite(cp) || !isFinite(ap) || cp <= 0) return '';
      const direction = calculatePLBasedSuggestion(row);
      if (direction === 'CASH' || direction === 'N/A') return '';
      const fee = (row.timeframe === '1h') ? 0.3 : 0.5;
      const percentChange = ((ap - cp) / cp) * 100;
      const percentNet = direction === 'LONG' ? percentChange - fee : percentChange + fee;
      if (direction === 'LONG') {
        return percentChange > fee ? `Win (${percentNet.toFixed(1)}%)` : `Loss (${percentNet.toFixed(1)}%)`;
      } else {
        return percentChange < -fee ? `Win (${(-percentNet).toFixed(1)}%)` : `Loss (${(-percentNet).toFixed(1)}%)`;
      }
    } catch {
      return '';
    }
  };

  // Compute actual direction based on price at prediction vs actual price
  // Returns 'Up', 'Down', 'Neutral' or '' when data is insufficient
  const getActualDirection = (row) => {
    try {
      const cp = parseFloat(row.current_price ?? row.currentPrice ?? row.price_at_prediction ?? row.priceAtPrediction ?? row.price);
      const ap = parseFloat(row.actual_price);
  if (!isFinite(cp) || !isFinite(ap) || cp === 0) return '';
  const relChange = (ap - cp) / cp; // relative change from prediction price
  if (Math.abs(relChange) < 0.001) return 'Neutral'; // < 0.5% treated as neutral
  return relChange > 0 ? 'Up' : 'Down';
    } catch {
      return '';
    }
  };

  // Compute signed percent change between actual and price at prediction
  // Returns a number representing percent (e.g., 1.23 for +1.23%), or undefined when insufficient
  const getActualChangePercent = (row) => {
    try {
      const cp = parseFloat(row.current_price ?? row.currentPrice ?? row.price_at_prediction ?? row.priceAtPrediction ?? row.price);
      const ap = parseFloat(row.actual_price);
      if (!isFinite(cp) || !isFinite(ap) || cp === 0) return undefined;
      const relChange = (ap - cp) / cp;
      return relChange * 100;
    } catch {
      return undefined;
    }
  };

  // Format signed percent with one decimal and sign, or empty string when missing
  const formatSignedPercent = (val) => {
    if (typeof val !== 'number' || !isFinite(val)) return '';
    const fixed = val.toFixed(1);
    // Ensure + sign for positive values
    return (val > 0 ? `+${fixed}` : fixed) + '%';
  };

  // Check if columns should be displayed based on data availability
  const hasActualData = rows.some(r => 
    r.actual_price !== null && 
    r.actual_price !== undefined && 
    r.actual_price !== '' && 
    r.actual_price !== 0
  );
  
  const hasPerformanceData = rows.some(r => 
    r.performance !== null && 
    r.performance !== undefined && 
    r.performance !== '' &&
    r.performance !== 0
  );

  const renderMode2 = () => {
    if (mode !== 2) return null;

    // Helpers: selection derived from mode2FocusedModels (empty => all selected)
  const mode2AllCanonModels = (mode2Summary || []).map(r => toCanonicalModelValue(r.model)).filter(Boolean);
    const isMode2Selected = (model) => {
      const canon = toCanonicalModelValue(model);
      if (!canon) return false;
      if (!Array.isArray(mode2FocusedModels) || mode2FocusedModels.length === 0) return true; // all selected by default
      return mode2FocusedModels.includes(canon);
    };
    const toggleMode2Row = (model) => {
      const canon = toCanonicalModelValue(model);
      if (!canon) return;
      const present = new Set(mode2AllCanonModels);
      const curSet = new Set((mode2FocusedModels && mode2FocusedModels.length) ? mode2FocusedModels : mode2AllCanonModels);
      if (curSet.has(canon)) curSet.delete(canon); else curSet.add(canon);
      // If all present are selected, collapse to [] (means all)
      const next = Array.from(curSet);
      setMode2FocusedModels(next.length === present.size ? [] : next);
    };
    
    const chartOptions = {
      chart: {
        type: 'candlestick',
        height: 450,
        zoom: { enabled: true },
        toolbar: { show: true }
      },
      dataLabels: {
        enabled: false,
      },
      markers: {
        size: 0,
      },
      title: {
        text: `Historical Accuracy for ${symbol} (${timeframe})`,
        align: 'left'
      },
      xaxis: {
        type: 'datetime',
        labels: {
          datetimeUTC: false,
        }
      },
      yaxis: {
        tooltip: {
          enabled: true
        }
      },
      tooltip: {
        shared: true,
        custom: function({ series, seriesIndex, dataPointIndex, w }) {
          const o = w.globals.seriesCandleO?.[seriesIndex]?.[dataPointIndex];
          const h = w.globals.seriesCandleH?.[seriesIndex]?.[dataPointIndex];
          const l = w.globals.seriesCandleL?.[seriesIndex]?.[dataPointIndex];
          const c = w.globals.seriesCandleC?.[seriesIndex]?.[dataPointIndex];
          const model = w.globals.initialSeries?.[seriesIndex]?.data?.[dataPointIndex]?.model || 'N/A';
          const score = w.globals.initialSeries?.[seriesIndex]?.data?.[dataPointIndex]?.score;
          
          let scoreHtml = '';
          if (score !== null && score !== undefined) {
            scoreHtml = `<div><strong>Score:</strong> ${score.toFixed(2)}</div>`;
          }
          
          return `<div class="apexcharts-tooltip-candlestick">
                    <div><strong>Model:</strong> ${model}</div>
                    <div><strong>Predicted Low:</strong> ${l}</div>
                    <div><strong>Predicted High:</strong> ${h}</div>
                    <div><strong>Actual Price:</strong> ${c}</div>
                    ${scoreHtml}
                  </div>`;
        }
      }
    };
    
    // Group chart rows by model
    const seriesByModel = mode2ChartRows.reduce((acc, row) => {
      if (!acc[row.model]) {
        acc[row.model] = {
          name: row.model,
          data: []
        };
      }
      
      const timestamp = new Date(row.date).getTime();
      
      // Candlestick format: [timestamp, open, high, low, close]
      // Here: open=low, high=high, low=low, close=actual
      // This visualizes the predicted range vs the actual outcome
      if (row.prediction_low !== null && row.prediction_high !== null && row.actual_price !== null) {
        acc[row.model].data.push({
          x: timestamp,
          y: [row.prediction_low, row.prediction_high, row.prediction_low, row.actual_price],
          model: row.model,
          score: row.score
        });
      }
      
      return acc;
    }, {});
    
    const chartSeries = Object.values(seriesByModel).filter(s => s.data.length > 0);
    
    return (
      <>
        <Row>
          <Col>
            <Card>
              <CardBody>
                <h4 className="card-title">Prediction vs Actual Chart</h4>
                {loading ? (
                  <div className="text-center">Loading chart...</div>
                ) : chartSeries.length > 0 ? (
                  <Chart
                    options={chartOptions}
                    series={chartSeries}
                    type="candlestick"
                    height={450}
                  />
                ) : (
                  <div className="text-center">No chart data available</div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
        
        <Row>
          <Col>
            <Card>
              <CardBody>
                <h4 className="card-title">Model Summary</h4>
    <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}></th>
                        <th>Model</th>
                        <th>Prediction Count</th>
                        <th>Neutral Predictions</th>
                        <th>Correct Direction</th>
                        <th>Wrong Direction</th>
      <th>Avg. Score</th>
      <th>Total P/L (%)</th>
                        <th>Prediction Range (%)</th>
                        <th>Actual Range (%)</th>
                        <th>Date Range</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
      <tr><td colSpan="11" className="text-center">Loading...</td></tr>
                      ) : mode2Summary.length > 0 ? (
                        mode2Summary.map((row, index) => {
                          const checked = isMode2Selected(row.model);
                          return (
                            <tr
                              key={index}
                              className={getModelRowClass(row.model)}
                              style={{ cursor: 'pointer' }}
                              onClick={() => toggleMode2Row(row.model)}
                              title={checked ? 'Click to hide from chart' : 'Click to show on chart'}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={checked}
                                  onChange={() => toggleMode2Row(row.model)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td><strong>{row.model}</strong></td>
                              <td>{row.prediction_count}</td>
          <td>{row.neutral_predictions ?? ''}</td>
          <td>{row.correct_direction ?? ''}</td>
          <td>{row.wrong_direction ?? ''}</td>
          <td>{row.avg_score !== null ? row.avg_score.toFixed(2) : 'N/A'}</td>
          <td><span className={typeof row.total_pnl === 'number' && row.total_pnl >= 0 ? 'text-success' : 'text-danger'}>{typeof row.total_pnl === 'number' ? (row.total_pnl >= 0 ? '+' : '') + (row.total_pnl * 100).toFixed(2) + '%' : ''}</span></td>
                              <td>{typeof row.avg_prediction_range === 'number' ? row.avg_prediction_range.toFixed(2) + '%' : ''}</td>
                              <td>{typeof row.avg_actual_range === 'number' ? row.avg_actual_range.toFixed(2) + '%' : ''}</td>
                              <td>{row.date_range}</td>
                            </tr>
                          );
                        })
                      ) : (
        <tr><td colSpan="11" className="text-center">No data available</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </>
    );
  };

  const renderMode3 = () => {
    if (mode !== 3) return null;

    // Helpers: selection derived from mode3FocusedModels (empty => all selected)
  const mode3AllCanonModels = (mode3Data || []).map(r => toCanonicalModelValue(r.model)).filter(Boolean);
    const isMode3Selected = (model) => {
      const canon = toCanonicalModelValue(model);
      if (!canon) return false;
      if (!Array.isArray(mode3FocusedModels) || mode3FocusedModels.length === 0) return true;
      return mode3FocusedModels.includes(canon);
    };
    const toggleMode3Row = (model) => {
      const canon = toCanonicalModelValue(model);
      if (!canon) return;
      const present = new Set(mode3AllCanonModels);
      const curSet = new Set((mode3FocusedModels && mode3FocusedModels.length) ? mode3FocusedModels : mode3AllCanonModels);
      if (curSet.has(canon)) curSet.delete(canon); else curSet.add(canon);
      const next = Array.from(curSet);
      setMode3FocusedModels(next.length === present.size ? [] : next);
    };
    
    const chartOptions = {
      chart: {
        type: 'bar',
        height: 350,
        toolbar: { show: true }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          endingShape: 'rounded'
        },
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent']
      },
      xaxis: {
        categories: mode3Data.map(d => d.model),
      },
      yaxis: {
        title: {
          text: 'Average Score'
        }
      },
      fill: {
        opacity: 1
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val.toFixed(2);
          }
        }
      },
      title: {
        text: `Model Performance Comparison for ${symbol} (${timeframe})`,
        align: 'left'
      }
    };
    
    // Series data: single series for average score
    const chartSeries = [{
      name: 'Average Score',
      data: mode3Data.map(d => d.avg_score !== null ? d.avg_score : 0)
    }];
    
    return (
      <>
        <Row>
          <Col>
            <Card>
              <CardBody>
                <h4 className="card-title">Average Score by Model</h4>
                {loading ? (
                  <div className="text-center">Loading chart...</div>
                ) : chartSeries[0].data.length > 0 ? (
                  <Chart
                    options={chartOptions}
                    series={chartSeries}
                    type="bar"
                    height={350}
                  />
                ) : (
                  <div className="text-center">No chart data available</div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
        
        <Row>
          <Col>
            <Card>
              <CardBody>
                <h4 className="card-title">Model Score Ranking</h4>
                <div className="table-responsive">
      <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}></th>
                        <th>Rank</th>
                        <th>Model</th>
                        <th>Prediction Count</th>
        <th>In Range</th>
        <th>Out of Range</th>
        <th>Average Score</th>
                        <th>Prediction Range (%)</th>
                        <th>Actual Range (%)</th>
                        <th>Date Range</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
        <tr><td colSpan="10" className="text-center">Loading...</td></tr>
                      ) : mode3Data.length > 0 ? (
                        mode3Data.map((row, index) => {
                          const checked = isMode3Selected(row.model);
                          return (
                            <tr
                              key={index}
                              className={getModelRowClass(row.model)}
                              style={{ cursor: 'pointer' }}
                              onClick={() => toggleMode3Row(row.model)}
                              title={checked ? 'Click to hide from chart' : 'Click to show on chart'}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={checked}
                                  onChange={() => toggleMode3Row(row.model)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td>{index + 1}</td>
                              <td><strong>{row.model}</strong></td>
                              <td>{row.prediction_count}</td>
          <td>{row.in_range_count ?? ''}</td>
          <td>{row.out_range_count ?? ''}</td>
          <td>{row.avg_score !== null ? row.avg_score.toFixed(2) : 'N/A'}</td>
                              <td>{typeof row.avg_prediction_range === 'number' ? row.avg_prediction_range.toFixed(2) + '%' : ''}</td>
                              <td>{typeof row.avg_actual_range === 'number' ? row.avg_actual_range.toFixed(2) + '%' : ''}</td>
                              <td>{row.date_range}</td>
                            </tr>
                          );
                        })
                      ) : (
        <tr><td colSpan="10" className="text-center">No data available</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <>
      {/* Custom styles for AI model color families */}
      <style>
        {`
          .claude-primary {
            background-color: #e8f5e8 !important;
            border-color: #c8e6c9 !important;
          }
          .claude-basic {
            background-color: #f4faf4 !important;
            border-color: #eef7ee !important;
          }
          /* Clean-mode bottom branding bar */
          .forecastnest-cleanbar {
            position: fixed;
            left: 0;
            right: 0;
            bottom: 0;
            height: 5vh;
            background: rgba(0,0,0,0.55);
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.95rem;
            z-index: 2147483647;
            pointer-events: none;
          }
          .forecastnest-watermark {
            position: fixed;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%) rotate(-30deg);
            width: 200vw;
            text-align: center;
            /* Make watermark visible but still unobtrusive */
            color: rgba(0,0,0,0.12); /* visible but not distracting */
            font-weight: 700; /* match PredictionDetail for consistent appearance */
            font-size: 1.2vw; /* match PredictionDetail so watermark looks identical */
            letter-spacing: 0.12em;
            pointer-events: none;
            z-index: 1; /* behind content */
            white-space: nowrap;
            user-select: none;
            mix-blend-mode: normal;
            filter: blur(0.3px); /* reduced blur so text reads better */
            opacity: 0.2; /* more visible while still subtle */
          }
          .gemini-primary {
            background-color: #fdf1db !important;
            border-color: #f4e1a6 !important;
          }
          .gemini-basic-1 {
            background-color: #fef8ed !important;
            border-color: #f9f0dc !important;
          }
          .gemini-basic-2 {
            background-color: #fefcf6 !important;
            border-color: #fbf7ea !important;
          }
          .gpt-primary {
            background-color: #e1f5fe !important;
            border-color: #b3e5fc !important;
          }
          .gpt-basic {
            background-color: #f0faff !important;
            border-color: #e6f7ff !important;
          }
          /* Make report content maximize width on small screens, centered on larger screens */
          @media (max-width: 992px) {
            .report-content { width: 100% !important; max-width: none !important; padding-left: 8px; padding-right: 8px; }
          }
          /* Shared wrapper for table/chart with responsive paddings */
          .report-block { padding: 1rem; background-color: #f8f9fa; border-radius: 8px; }
          @media (max-width: 992px) {
            .report-block { padding: 8px; }
          }
        `}
      </style>
      
  <Container fluid className="p-0" style={{ height: '100vh', overflow: 'hidden' }}>
        <Row className="g-0" style={{ height: '100%' }}>
          {/* Left Sidebar for Controls - conditionally rendered */}
          {showOptionsPanel && (
            <Col lg="4" xl="3" className="bg-light border-end" style={{ height: '100vh', overflowY: 'auto', position: 'relative' }}>
              <div className="p-3" style={{ paddingBottom: '90px' }}>
                <h6 className="text-primary mb-3">Report Settings</h6>
            
            {/* Query Controls */}
            <div className="mb-4">
              <h6 className="text-dark mb-3">Query Parameters</h6>

            {/* Report Mode */}
              <div className="mb-3">
                <div className="row align-items-center mb-2">
                  <div className="col-4">
                    <label className="text-muted small mb-0">Mode</label>
                  </div>
                  <div className="col-8">
                    <Input type="select" size="sm" value={mode} onChange={(e) => { const m = parseInt(e.target.value, 10) || 1; setMode(m); lastFetchKeyRef.current = ''; }}>
                      <option value={1}>Current Predictions</option>
                      <option value={2}>Prediction History</option>
                      <option value={3}>Score Report</option>
                    </Input>
                  </div>
                </div>
                
                {/* Performance Mode Toggle (Mode 1 only) */}
                {mode === 1 && (
                  <div className="row align-items-center ps-2">
                    <div className="col-12">
                      <div className="d-flex align-items-center gap-2">
                        <label className="form-check-label d-flex align-items-center text-muted small mb-0" style={{ cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            className="form-check-input me-2"
                            checked={isPerformanceMode}
                            onChange={handlePerformanceModeToggle}
                          />
                          <span>Performance Metrics</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mb-2">
                <div className="row align-items-center">
                  <div className="col-4">
                    <label className="text-muted small mb-0">Symbol</label>
                  </div>
                  <div className="col-8">
                    <Input type="select" size="sm" value={symbol} onChange={(e) => setSymbol(e.target.value)} disabled={symbolsLoading}>
                      {sortedAvailableSymbols.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </Input>
                  </div>
                </div>
              </div>
              
              <div className="mb-2">
                <div className="row align-items-center">
                  <div className="col-4">
                    <label className="text-muted small mb-0">Timeframe</label>
                  </div>
                  <div className="col-8">
                    <Input type="select" size="sm" value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
                      {timeframes.map((tf) => (
                        <option key={tf || 'all'} value={tf}>{tf || 'All'}</option>
                      ))}
                    </Input>
                  </div>
                </div>
              </div>

              {/* Hour Selection for 4h timeframe */}
              {timeframe === '4h' && (
                <div className="mb-2">
                  <label className="text-muted small mb-1 d-block">Hour Selections</label>
                  <div className="row g-1">
                    {hourOptions.map(hour => (
                      <div key={hour} className="col-2">
                        <label className="form-check-label d-flex align-items-center" style={{ cursor: 'pointer', fontSize: '0.75rem' }}>
                          <input
                            type="checkbox"
                            className="form-check-input me-1"
                            checked={selectedHours.includes(hour)}
                            onChange={() => toggleHour(hour)}
                          />
                          <span>{formatHourLabel(hour)}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="mb-2">
                <div className="row align-items-center">
                  <div className="col-4">
                    <label className="text-muted small mb-0">From Date</label>
                  </div>
                  <div className="col-8">
                    <Input type="date" size="sm" value={from} onChange={(e) => setFrom(e.target.value)} />
                  </div>
                </div>
              </div>
              
              <div className="mb-2">
                <div className="row align-items-center">
                  <div className="col-4">
                    <label className="text-muted small mb-0">To Date</label>
                  </div>
                  <div className="col-8">
                    <Input type="date" size="sm" value={to} onChange={(e) => setTo(e.target.value)} />
                  </div>
                </div>
              </div>
              
            </div>

            {/* Chart Type Selection (Mode 1 only) - Hidden */}
            {false && (
            <div className="mb-4">
              <h6 className="text-dark mb-3">Chart Display</h6>
              
              <div className="mb-2">
                <label className="text-muted small mb-2 d-block">Chart Type</label>
                <div className="d-flex flex-column gap-2">
                  <label className="form-check-label d-flex align-items-center" style={{ cursor: 'pointer', fontSize: '0.8rem' }}>
                    <input
                      type="radio"
                      className="form-check-input me-2"
                      name="chartType"
                      value="dashboard-crypto"
                      checked={chartType === 'dashboard-crypto'}
                      onChange={(e) => setChartType(e.target.value)}
                      disabled={mode !== 1}
                    />
                    <span>ApexCharts (Database OHLCV)</span>
                  </label>
                  <label className="form-check-label d-flex align-items-center" style={{ cursor: 'pointer', fontSize: '0.8rem' }}>
                    <input
                      type="radio"
                      className="form-check-input me-2"
                      name="chartType"
                      value="tradingview"
                      checked={chartType === 'tradingview'}
                      onChange={(e) => setChartType(e.target.value)}
                      disabled={mode !== 1}
                    />
                    <span>TradingView Chart</span>
                  </label>
                </div>
              </div>
              
            </div>
            )}

            {/* Mode 2 specific options */}
            {mode === 2 && (
              <div className="mb-4">
                <h6 className="text-dark mb-3">Mode 2 Options</h6>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="toggleM2ActualPrice"
                    checked={mode2ShowActualPrice}
                    onChange={(e) => setMode2ShowActualPrice(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="toggleM2ActualPrice">
                    Show Actual Price Line
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="toggleM2ActualRange"
                    checked={mode2ShowActualRange}
                    onChange={(e) => setMode2ShowActualRange(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="toggleM2ActualRange">
                    Show Actual Price Range
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="toggleM2CumulativePnL"
                    checked={mode2ShowCumulativePnL}
                    onChange={(e) => setMode2ShowCumulativePnL(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="toggleM2CumulativePnL">
                    Show Cumulative P/L
                  </label>
                </div>
              </div>
            )}

            {/* Mode 2/3 focus options removed; selection is now controlled by the report tables */}

            {/* Primary Models */}
            <div className="mb-3">
              <h6 className="text-dark mb-2">Primary Models</h6>
              <div className="row g-1" style={{ maxHeight: 200, overflowY: 'auto' }}>
                {PRIMARY_MODELS.map(m => (
                  <div key={m.value} className="col-6">
                    <label className="form-check-label d-flex align-items-center" style={{ cursor: 'pointer', fontSize: '0.8rem' }}>
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={selectedModels.includes(m.value)}
                        onChange={() => toggleModel(m.value)}
                      />
                      <span>{m.label}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Basic Models */}
            <div className="mb-3">
              <h6 className="text-dark mb-2">Basic Models</h6>
              <div className="row g-1" style={{ maxHeight: 200, overflowY: 'auto' }}>
                {BASIC_MODELS.map(m => (
                  <div key={m.value} className="col-6">
                    <label className="form-check-label d-flex align-items-center" style={{ cursor: 'pointer', fontSize: '0.8rem' }}>
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={selectedModels.includes(m.value)}
                        onChange={() => toggleModel(m.value)}
                      />
                      <span>{m.label}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer actions: Apply and Hide Options */}
            <div className="mt-3 pt-2" style={{ position: 'sticky', bottom: 0, background: '#f8f9fa', borderTop: '1px solid #dee2e6' }}>
              <div className="row g-2">
                <div className="col-6">
                  <Button color="primary" size="sm" onClick={onApply} disabled={loading} className="w-100">
                    {loading ? 'Loading…' : 'Apply'}
                  </Button>
                </div>
                <div className="col-6">
                  <Button color="secondary" size="sm" outline onClick={() => toggleOptionsPanel(window.location.search)} className="w-100">
                    Hide Options
                  </Button>
                </div>
              </div>
            </div>

            {/* Backtest Section */}
            {mode === 2 && (
              <div className="mt-4 pt-3 border-top">
                <h6 className="text-dark mb-3">
                  <i className="ri-bar-chart-box-line me-2"></i>Backtest Settings
                </h6>

                {/* Strategy Selection */}
                <div className="mb-3">
                  <label className="text-muted small mb-2 d-block">Trade Strategy</label>
                  <Input 
                    type="select" 
                    size="sm"
                    value={backtestStrategy}
                    onChange={(e) => setBacktestStrategy(e.target.value)}
                  >
                    {BACKTEST_STRATEGIES.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </Input>
                  <small className="text-muted d-block mt-1">
                    {BACKTEST_STRATEGIES.find(s => s.value === backtestStrategy)?.description}
                  </small>
                </div>

                {/* Cumulative Profile Toggle */}
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="toggleCumulativeProfile"
                    checked={showCumulativeProfile}
                    onChange={(e) => setShowCumulativeProfile(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="toggleCumulativeProfile">
                    Show Cumulative P/L in Chart
                  </label>
                </div>

                {/* Perform Backtest Button */}
                <Button
                  color="primary"
                  size="sm"
                  className="w-100"
                  onClick={handlePerformBacktest}
                >
                  <i className="ri-play-line me-2"></i>Perform Backtest
                </Button>
              </div>
            )}
          </div>
        </Col>
          )}

  {/* Main Content Area */}
  <Col lg={showOptionsPanel ? "8" : "12"} xl={showOptionsPanel ? "9" : "12"} style={{ height: '100vh', overflowY: 'auto' }}>
          <div className="px-2 px-sm-3 px-lg-3 py-4">
            {/* Logo/Brand at top */}
            <div className="text-center mb-2 pt-2">
              <img src={logoLight} alt="ForecastNest" height="32" />
            </div>
            
            {/* Centered Report Title */}
            <div className="text-center mb-3 mt-3">
              <h2 className="mb-3 text-primary">
                {mode === 1 && isPerformanceMode && (<span>{symbol} Model Performance Metrics</span>)}
                {mode === 1 && !isPerformanceMode && (<span>{symbol} Predictions for {formatPredictionDate()}</span>)}
                {mode === 2 && (<span>{symbol} Prediction History</span>)}
                {mode === 3 && (<span>{symbol} Model Score Report</span>)}
              </h2>
              <p className="text-muted mb-0 fs-6">
                {mode === 1 ? (
                  <>Predicted by {formatSelectedModels()} • Refreshed On {formatCreationTime()}</>
                ) : (
                  <>Timeframe: {timeframe || 'All'} • Filters applied via URL • Refreshed On {formatCreationTime()}</>
                )}
              </p>
              <div className="mx-auto mt-3" style={{ maxWidth: 720 }}>
                <ResearchDisclaimer />
              </div>
            </div>

            {/* Error Message */}
            {errorMsg && (
              <div className="alert alert-warning py-3 mb-4">{errorMsg}</div>
            )}

            {/* Content Container: maintain reasonable max width, centered when panel is hidden */}
            <div className="report-content" style={{ 
              width: showOptionsPanel ? '100%' : '92%', 
              maxWidth: showOptionsPanel ? 'none' : '1600px',
              margin: '0 auto'
            }}>
              {/* Mode 1: Table Section (Predictions or Performance Metrics) */}
              {mode === 1 && !isPerformanceMode && (
              <div className="mb-2">
                <div className="report-block table-wrap" style={{ overflowX: 'auto' }}>
                  <table className="table table-striped align-middle bg-white" style={{ minWidth: 900, marginBottom: 0 }}>
                    <thead>
                      {/* Header Row 1: Section titles */}
                      <tr>
                        {(() => {
                          // Predictions group columns: Model, Version, TF, Create, Target, Low, Price, High, Range (%), Conf%, Signal
                          const predictionsColSpan = 11;
                          // Actual group columns are conditional
                          let actualColSpan = 0;
                          if (hasActualData) {
                            // Actual Price, Change (%), (Low, High if performance), Actual Range (%), (Score if performance)
                            actualColSpan = 2 + (hasPerformanceData ? 2 : 0) + 1 + (hasPerformanceData ? 1 : 0);
                          }
                          return (
                            <>
                              <th colSpan={predictionsColSpan} className="text-center text-muted">Predictions</th>
                              {hasActualData && <th colSpan={actualColSpan} className="text-center">Result</th>}
                            </>
                          );
                        })()}
                      </tr>
                      {/* Header Row 2: Simplified labels */}
                      <tr>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('model')}>
                          Model {getSortIndicator('model')}
                        </th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>
                          Version
                        </th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('timeframe')}>
                          TF {getSortIndicator('timeframe')}
                        </th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('create_date')}>
                          Create (UTC) {getSortIndicator('create_date')}
                        </th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('target_date')}>
                          Target (UTC) {getSortIndicator('target_date')}
                        </th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('prediction_low')}>
                          Low {getSortIndicator('prediction_low')}
                        </th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('current_price')}>
                          Current {getSortIndicator('current_price')}
                        </th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('prediction_high')}>
                          High {getSortIndicator('prediction_high')}
                        </th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>
                          Range (%)
                        </th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>
                          ATR (%)
                        </th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('confidence')}>
                          Conf% {getSortIndicator('confidence')}
                        </th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('signal')}>
                          Signal {getSortIndicator('signal')}
                        </th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>
                          Suggestion
                        </th>
                        {hasActualData && (
                          <>
                            <th scope="col" style={{ padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('actual_price')}>
                              Price {getSortIndicator('actual_price')}
                            </th>
                            <th scope="col" style={{ padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('actual_dir')}>
                              Change (%) {getSortIndicator('actual_dir')}
                            </th>
                            {hasPerformanceData && (
                              <>
                                <th scope="col" style={{ padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('actual_low')}>
                                  Low {getSortIndicator('actual_low')}
                                </th>
                                <th scope="col" style={{ padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('actual_high')}>
                                  High {getSortIndicator('actual_high')}
                                </th>
                              </>
                            )}
                            <th scope="col" style={{ padding: '12px 8px' }}>
                              Actual Range (%)
                            </th>
                            {hasPerformanceData && (
                              <th scope="col" style={{ padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('performance')}>
                                Score {getSortIndicator('performance')}
                              </th>
                            )}
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={12 + (hasActualData ? (2 + (hasPerformanceData ? 2 : 0) + 1 + (hasPerformanceData ? 1 : 0)) : 0)} className="text-center text-muted" style={{ padding: '20px 8px' }}>No data</td>
                        </tr>
                      )}
                      {rows.map((r) => (
                        <tr key={r.id} className={getModelRowClass(r.model)}
                            onClick={() => handleRowClick(r.id)}
                            style={{ cursor: 'pointer' }}
                            title="Click to view prediction details">
                          <td className="text-muted" style={{ padding: '12px 8px' }}><strong>{r.model}</strong></td>
                          <td className="text-muted" style={{ padding: '12px 8px', fontSize: '12px' }}>{r.version || '-'}</td>
                          <td className="text-muted" style={{ padding: '12px 8px' }}>{r.timeframe}</td>
                          <td className="text-muted" style={{ padding: '12px 8px', backgroundColor: isWeekend(r.create_date) ? '#ffcccc' : 'transparent', color: isWeekend(r.create_date) ? '#c62828' : 'inherit', fontWeight: isWeekend(r.create_date) ? '600' : 'normal' }}>{(r.create_date || '').toString().slice(0, 16).replace('T', ' ')}</td>
                          <td className="text-muted" style={{ padding: '12px 8px' }}>{(r.target_date || '').toString().slice(0, 16).replace('T', ' ')}</td>
                          <td className="text-muted" style={{ padding: '12px 8px' }}>{formatPrice(r.prediction_low)}</td>
                          <td className="text-muted" style={{ padding: '12px 8px' }}>{formatPrice(r.current_price ?? r.currentPrice ?? r.price_at_prediction ?? r.priceAtPrediction ?? r.price)}</td>
                          <td className="text-muted" style={{ padding: '12px 8px' }}>{formatPrice(r.prediction_high)}</td>
                          <td className="text-muted" style={{ padding: '12px 8px' }}>{formatPredChangeRange(r)}</td>
                          <td className="text-muted" style={{ padding: '12px 8px' }}>{formatATRPercent(r)}</td>
                          <td className="text-muted" style={{ padding: '12px 8px' }}>{formatConfidence(r.confidence)}</td>
                          <td className="text-muted" style={{ padding: '12px 8px' }}>{((r.signal || '') || getFallbackSignal(r)).toString().toUpperCase() || ''}</td>
                          <td className="text-muted" style={{ padding: '12px 8px', fontWeight: 600, color: getSuggestion(r) === 'LONG' ? '#2e7d32' : (getSuggestion(r) === 'SHORT' ? '#c62828' : '#666') }}>{getSuggestion(r)}</td>
                          {hasActualData && <td style={{ padding: '12px 8px' }}>{formatPrice(r.actual_price)}</td>}
                          {hasActualData && (
                            <td style={{ padding: '12px 8px' }}>
                              {formatSignedPercent(getActualChangePercent(r))}
                            </td>
                          )}
                          {hasPerformanceData && (
                            <>
                              <td style={{ padding: '12px 8px' }}>{formatPrice(r.actual_low)}</td>
                              <td style={{ padding: '12px 8px' }}>{formatPrice(r.actual_high)}</td>
                            </>
                          )}
                          {hasActualData && <td style={{ padding: '12px 8px' }}>{formatActualChangeRange(r)}</td>}
                          {hasPerformanceData && <td style={{ padding: '12px 8px' }}>{formatPerformance(r.performance)}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              )}

              {/* Mode 1: Performance Metrics Table */}
              {mode === 1 && isPerformanceMode && (
              <div className="mb-2">
                <div className="report-block table-wrap" style={{ overflowX: 'auto' }}>
                  <table className="table table-striped align-middle bg-white" style={{ minWidth: 1400, marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('model')}>
                          Model {getSortIndicator('model')}
                        </th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>
                          Version
                        </th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('timeframe')}>
                          TF {getSortIndicator('timeframe')}
                        </th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('create_date')}>
                          Create (UTC) {getSortIndicator('create_date')}
                        </th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>Suggestion</th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>Confidence</th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>Status</th>
                        <th className="text-center text-muted" colSpan={3} style={{ padding: '12px 8px' }}>3 Days</th>
                        <th className="text-center text-muted" colSpan={3} style={{ padding: '12px 8px', borderLeft: '2px solid #ddd' }}>7 Days</th>
                        <th className="text-center text-muted" colSpan={3} style={{ padding: '12px 8px', borderLeft: '2px solid #ddd' }}>30 Days</th>
                        <th className="text-center text-muted" colSpan={3} style={{ padding: '12px 8px', borderLeft: '2px solid #ddd' }}>90 Days</th>
                      </tr>
                      <tr>
                        <th colSpan={6}></th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>PL%</th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>WR%</th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>PB%</th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px', borderLeft: '2px solid #ddd' }}>PL%</th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>WR%</th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>PB%</th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px', borderLeft: '2px solid #ddd' }}>PL%</th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>WR%</th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>PB%</th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px', borderLeft: '2px solid #ddd' }}>PL%</th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>WR%</th>
                        <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>PB%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={18} className="text-center text-muted" style={{ padding: '20px 8px' }}>No data</td>
                        </tr>
                      )}
                      {rows.map((r) => (
                        <tr key={r.id} className={getModelRowClass(r.model)} style={{ cursor: 'pointer' }} title="Click to view prediction details" onClick={() => handleRowClick(r.id)}>
                          <td className="text-muted" style={{ padding: '12px 8px' }}><strong>{r.model}</strong></td>
                          <td className="text-muted" style={{ padding: '12px 8px', fontSize: '12px' }}>{r.version || '-'}</td>
                          <td className="text-muted" style={{ padding: '12px 8px' }}>{r.timeframe}</td>
                          <td className="text-muted" style={{ padding: '12px 8px', backgroundColor: isWeekend(r.create_date) ? '#ffcccc' : 'transparent', color: isWeekend(r.create_date) ? '#c62828' : 'inherit', fontWeight: isWeekend(r.create_date) ? '600' : 'normal' }}>{(r.create_date || '').toString().slice(0, 16).replace('T', ' ')}</td>
                          <td style={{ 
                            padding: '12px 8px', 
                            fontWeight: 'bold',
                            textAlign: 'center',
                            color: (() => {
                              const sugg = calculatePLBasedSuggestion(r);
                              if (sugg === 'LONG') return '#2e7d32';
                              if (sugg === 'SHORT') return '#c62828';
                              if (sugg === 'CASH') return '#1565c0';
                              return '#666';
                            })()
                          }}>
                            {calculatePLBasedSuggestion(r)}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            {formatConfidence(r.confidence) ? formatConfidence(r.confidence) + '%' : '—'}
                          </td>
                          <td style={{
                            padding: '12px 8px',
                            textAlign: 'center',
                            fontWeight: 600,
                            color: getPredictionStatus(r).startsWith('Win') ? '#2e7d32' : getPredictionStatus(r).startsWith('Loss') ? '#c62828' : '#666'
                          }}>
                            {getPredictionStatus(r) || '—'}
                          </td>
                          {/* 3 Days */}
                          <td style={{ padding: '12px 8px', textAlign: 'center', color: typeof r['3dpl'] === 'number' && r['3dpl'] >= 0 ? '#2e7d32' : '#c62828' }}>
                            {typeof r['3dpl'] === 'number' ? (r['3dpl'] >= 0 ? '+' : '') + r['3dpl'].toFixed(2) : '—'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            {typeof r['3dwr'] === 'number' ? r['3dwr'].toFixed(2) : '—'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center', color: typeof r['3dpb'] === 'number' && r['3dpb'] < 0 ? '#c62828' : '#666' }}>
                            {typeof r['3dpb'] === 'number' ? r['3dpb'].toFixed(2) : '—'}
                          </td>
                          {/* 7 Days */}
                          <td style={{ padding: '12px 8px', textAlign: 'center', color: typeof r['7dpl'] === 'number' && r['7dpl'] >= 0 ? '#2e7d32' : '#c62828', borderLeft: '2px solid #ddd' }}>
                            {typeof r['7dpl'] === 'number' ? (r['7dpl'] >= 0 ? '+' : '') + r['7dpl'].toFixed(2) : '—'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            {typeof r['7dwr'] === 'number' ? r['7dwr'].toFixed(2) : '—'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center', color: typeof r['7dpb'] === 'number' && r['7dpb'] < 0 ? '#c62828' : '#666' }}>
                            {typeof r['7dpb'] === 'number' ? r['7dpb'].toFixed(2) : '—'}
                          </td>
                          {/* 30 Days */}
                          <td style={{ padding: '12px 8px', textAlign: 'center', color: typeof r['30dpl'] === 'number' && r['30dpl'] >= 0 ? '#2e7d32' : '#c62828', borderLeft: '2px solid #ddd' }}>
                            {typeof r['30dpl'] === 'number' ? (r['30dpl'] >= 0 ? '+' : '') + r['30dpl'].toFixed(2) : '—'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            {typeof r['30dwr'] === 'number' ? r['30dwr'].toFixed(2) : '—'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center', color: typeof r['30dpb'] === 'number' && r['30dpb'] < 0 ? '#c62828' : '#666' }}>
                            {typeof r['30dpb'] === 'number' ? r['30dpb'].toFixed(2) : '—'}
                          </td>
                          {/* 90 Days */}
                          <td style={{ padding: '12px 8px', textAlign: 'center', color: typeof r['90dpl'] === 'number' && r['90dpl'] >= 0 ? '#2e7d32' : '#c62828', borderLeft: '2px solid #ddd' }}>
                            {typeof r['90dpl'] === 'number' ? (r['90dpl'] >= 0 ? '+' : '') + r['90dpl'].toFixed(2) : '—'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            {typeof r['90dwr'] === 'number' ? r['90dwr'].toFixed(2) : '—'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center', color: typeof r['90dpb'] === 'number' && r['90dpb'] < 0 ? '#c62828' : '#666' }}>
                            {typeof r['90dpb'] === 'number' ? r['90dpb'].toFixed(2) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              )}

              {/* Mode 2 summary table */}
              {mode === 2 && (
                <div className="mb-4">
                  <div className="report-block table-wrap" style={{ overflowX: 'auto' }}>
                    <table className="table table-striped align-middle bg-white" style={{ minWidth: 680, marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th style={{ width: 36 }}></th>
                          <th style={{ padding: '12px 8px' }}>Model</th>
                          <th style={{ padding: '12px 8px' }}>Predictions</th>
                          <th style={{ padding: '12px 8px' }}>Neutral Predictions</th>
                          <th style={{ padding: '12px 8px' }}>Correct Direction</th>
                          <th style={{ padding: '12px 8px' }}>Wrong Direction</th>
                          <th style={{ padding: '12px 8px' }}>Avg Score</th>
                          <th style={{ padding: '12px 8px' }}>Total P/L (%)</th>
                          <th style={{ padding: '12px 8px' }}>Prediction Range (%)</th>
                          <th style={{ padding: '12px 8px' }}>Actual Range (%)</th>
                          <th style={{ padding: '12px 8px' }}>Date Range</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(mode2Summary || []).length === 0 && (
                          <tr><td colSpan={11} className="text-center text-muted" style={{ padding: '20px 8px' }}>No data</td></tr>
                        )}
                        {(mode2Summary || []).map((r, idx) => {
                          const checked = isMode2Selected(r.model);
                          return (
                            <tr
                              key={idx}
                              className={getModelRowClass(r.model)}
                              style={{ cursor: 'pointer' }}
                              onClick={() => toggleMode2Row(r.model)}
                              title={checked ? 'Click to hide from chart' : 'Click to show on chart'}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={checked}
                                  onChange={() => toggleMode2Row(r.model)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td style={{ padding: '12px 8px' }}><strong>{r.model}</strong></td>
                              <td style={{ padding: '12px 8px' }}>{r.prediction_count}</td>
                              <td style={{ padding: '12px 8px' }}>{r.neutral_predictions ?? ''}</td>
                              <td style={{ padding: '12px 8px' }}>{r.correct_direction ?? ''}</td>
                              <td style={{ padding: '12px 8px' }}>{r.wrong_direction ?? ''}</td>
                              <td style={{ padding: '12px 8px' }}>{typeof r.avg_score === 'number' ? Math.round(r.avg_score) : ''}</td>
                              <td style={{ padding: '12px 8px' }}><span className={typeof r.total_pnl === 'number' && r.total_pnl >= 0 ? 'text-success' : 'text-danger'}>{typeof r.total_pnl === 'number' ? (r.total_pnl >= 0 ? '+' : '') + (r.total_pnl * 100).toFixed(2) + '%' : ''}</span></td>
                              <td style={{ padding: '12px 8px' }}>{typeof r.avg_prediction_range === 'number' ? r.avg_prediction_range.toFixed(2) + '%' : ''}</td>
                              <td style={{ padding: '12px 8px' }}>{typeof r.avg_actual_range === 'number' ? r.avg_actual_range.toFixed(2) + '%' : ''}</td>
                              <td style={{ padding: '12px 8px' }}>{r.date_range || ''}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Mode 3 table */}
              {mode === 3 && (
                <div className="mb-4">
                  <div className="report-block table-wrap" style={{ overflowX: 'auto' }}>
                    <table className="table table-striped align-middle bg-white" style={{ minWidth: 680, marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th style={{ width: 36 }}></th>
                          <th style={{ padding: '12px 8px' }}>Model</th>
                          <th style={{ padding: '12px 8px' }}>Predictions</th>
                          <th style={{ padding: '12px 8px' }}>In Range</th>
                          <th style={{ padding: '12px 8px' }}>Out of Range</th>
                          <th style={{ padding: '12px 8px' }}>Avg Score</th>
                          <th style={{ padding: '12px 8px' }}>Date Range</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(mode3Data || []).length === 0 && (
                          <tr><td colSpan={7} className="text-center text-muted" style={{ padding: '20px 8px' }}>No data</td></tr>
                        )}
                        {(mode3Data || []).map((r, idx) => {
                          const checked = isMode3Selected(r.model);
                          return (
                            <tr
                              key={idx}
                              className={getModelRowClass(r.model)}
                              style={{ cursor: 'pointer' }}
                              onClick={() => toggleMode3Row(r.model)}
                              title={checked ? 'Click to hide from chart' : 'Click to show on chart'}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={checked}
                                  onChange={() => toggleMode3Row(r.model)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td style={{ padding: '12px 8px' }}><strong>{r.model}</strong></td>
                              <td style={{ padding: '12px 8px' }}>{r.prediction_count}</td>
                              <td style={{ padding: '12px 8px' }}>{r.in_range_count ?? ''}</td>
                              <td style={{ padding: '12px 8px' }}>{r.out_range_count ?? ''}</td>
                              <td style={{ padding: '12px 8px' }}>{typeof r.avg_score === 'number' ? Math.round(r.avg_score) : (typeof r.avg_score === 'string' ? Math.round(parseFloat(r.avg_score)) : '')}</td>
                              <td style={{ padding: '12px 8px' }}>{r.date_range || `${formatDisplayDate(r.min_date || '')} to ${formatDisplayDate(r.max_date || '')}`}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Chart / Visualization Section */}
              <div className="mb-3 report-block chart-wrap">
                <div style={{ minHeight: '300px' }}>
                  {mode === 1 && (
                    <div style={{ height: '300px' }}>
                      {chartType === 'tradingview' ? (
                        <TradingViewChart
                          symbol={tvSymbol}
                          interval={tvInterval}
                          autosize={true}
                          theme="dark"
                          height={300}
                          onReady={(w) => {
                            tvWidgetRef.current = w;
                            try {
                              const chart = w && w.chart && w.chart();
                              if (chart && typeof chart.setSymbol === 'function') {
                                chart.setSymbol(tvSymbol, tvInterval);
                                setTimeout(() => { try { chart.setSymbol(tvSymbol, tvInterval); } catch (e) {} }, 150);
                              }
                            } catch (e) {}
                            removeAllStudies();
                            drawPredictionShapes();
                            ensurePriceSymbol(tvSymbol, tvInterval);
                          }}
                        />
                      ) : (
                        <div style={{ height: '100%', overflow: 'hidden' }}>
                          <ReportMarketGraph 
                            symbol={chartSymbol}
                            timeframe={chartTimeframe}
                            from={from}
                            to={to}
                            // Pass deduplicated predictions only
                            predictions={dedupedRows}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {mode === 2 && (
                    <>
                      <div style={{ height: '300px' }}>
                        {mode2Series.length === 0 ? (
                          <div className="text-center text-muted py-4">
                            <div>No historical data for the selected filters.</div>
                            <div className="small mt-2">Try widening the date range or removing model filters. The page will auto-expand to 60 days and retry without models once.</div>
                            <div className="small mt-2">Filters: symbol={symbol}, timeframe={timeframe || 'all'}, from={from || '(none)'}, to={to || '(none)'}.</div>
                          </div>
                        ) : (
                          <Chart options={mode2Options} series={mode2Series} type={mode2Series?.[0]?.type || 'line'} height={300} />
                        )}
                      </div>

                      {/* Cumulative P/L Chart for Mode 2 */}
                      {showCumulativeProfile && backtestResults && backtestResults.cumulativeReturns.length > 0 && (
                        <div style={{ height: '250px', marginTop: '12px' }}>
                          <Chart
                            options={{
                              chart: {
                                type: 'line',
                                height: 250,
                                foreColor: '#adb5bd',
                                toolbar: { show: true },
                                background: 'transparent'
                              },
                              title: {
                                text: `Cumulative P/L — ${backtestStrategy} Strategy (${backtestResults.totalTrades} trades)`,
                                align: 'left',
                                style: { color: '#adb5bd' }
                              },
                              stroke: {
                                curve: 'smooth',
                                width: 2
                              },
                              colors: [backtestResults.totalReturn >= 0 ? '#43a047' : '#e53935'],
                              xaxis: {
                                type: 'datetime',
                                labels: { rotate: -15 }
                              },
                              yaxis: {
                                title: { text: 'P/L %', style: { color: '#adb5bd' } },
                                labels: { formatter: (v) => v.toFixed(2) + '%' }
                              },
                              tooltip: {
                                theme: 'dark',
                                y: { formatter: (v) => v.toFixed(2) + '%' }
                              },
                              legend: { show: false }
                            }}
                            series={[{
                              name: 'Cumulative P/L',
                              data: backtestResults.cumulativeReturns.map(r => ({
                                x: new Date(r.date).getTime(),
                                y: r.value
                              }))
                            }]}
                            type="line"
                          />
                        </div>
                      )}

                      {/* Backtest Results Table */}
                      {backtestPerformed && backtestResults && (
                        <div style={{ marginTop: '20px' }}>
                          <h6 className="text-dark mb-3">Backtest Results — {backtestStrategy}</h6>
                          <Card className="border-0 shadow-sm">
                            <CardBody>
                              <div className="row text-center">
                                <div className="col-md-3">
                                  <div className="mb-3">
                                    <span className="text-muted small d-block mb-1">Total Trades</span>
                                    <h4 className="mb-0">{backtestResults.totalTrades}</h4>
                                  </div>
                                </div>
                                <div className="col-md-3">
                                  <div className="mb-3">
                                    <span className="text-muted small d-block mb-1">Winning Trades</span>
                                    <h4 className="text-success mb-0">{backtestResults.winningTrades}</h4>
                                  </div>
                                </div>
                                <div className="col-md-3">
                                  <div className="mb-3">
                                    <span className="text-muted small d-block mb-1">Losing Trades</span>
                                    <h4 className="text-danger mb-0">{backtestResults.losingTrades}</h4>
                                  </div>
                                </div>
                                <div className="col-md-3">
                                  <div className="mb-3">
                                    <span className="text-muted small d-block mb-1">Win Rate</span>
                                    <h4 className="mb-0">{backtestResults.winRate}%</h4>
                                  </div>
                                </div>
                              </div>
                              <div className="row text-center mt-3 pt-3 border-top">
                                <div className="col-md-6">
                                  <div className="mb-3">
                                    <span className="text-muted small d-block mb-1">Total Return</span>
                                    <h5 className={backtestResults.totalReturn >= 0 ? 'text-success mb-0' : 'text-danger mb-0'}>
                                      {backtestResults.totalReturn.toFixed(2)}%
                                    </h5>
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="mb-3">
                                    <span className="text-muted small d-block mb-1">Average Return per Trade</span>
                                    <h5 className={backtestResults.avgReturnPerTrade >= 0 ? 'text-success mb-0' : 'text-danger mb-0'}>
                                      {backtestResults.avgReturnPerTrade ? backtestResults.avgReturnPerTrade.toFixed(2) + '%' : 'N/A'}
                                    </h5>
                                  </div>
                                </div>
                              </div>
                            </CardBody>
                          </Card>

                          {/* Backtest: Historical Trades Table */}
                          {backtestResults.tradeDetails && backtestResults.tradeDetails.length > 0 && (
                            <div style={{ marginTop: '20px' }}>
                              <h6 className="text-dark mb-3">Backtest: Historical Trades</h6>
                              <div className="table-responsive">
                                <table className="table table-striped align-middle bg-white" style={{ minWidth: 600, marginBottom: 0 }}>
                                  <thead>
                                    <tr>
                                      <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>Date</th>
                                      <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>Signal</th>
                                      <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>Entry</th>
                                      <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>Exit</th>
                                      <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>Return %</th>
                                      <th scope="col" className="text-muted" style={{ padding: '12px 8px' }}>Cumulative Return %</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {backtestResults.tradeDetails.map((trade, idx) => (
                                      <tr key={idx}>
                                        <td className="text-muted" style={{ padding: '12px 8px', backgroundColor: isWeekend(trade.date) ? '#ffcccc' : 'transparent', color: isWeekend(trade.date) ? '#c62828' : 'inherit', fontWeight: isWeekend(trade.date) ? '600' : 'normal' }}>
                                          {typeof trade.date === 'string' ? trade.date.slice(0, 16).replace('T', ' ') : trade.date}
                                        </td>
                                        <td style={{ padding: '12px 8px', fontWeight: '600', color: trade.signal === 'LONG' ? '#2e7d32' : '#c62828' }}>{trade.signal}</td>
                                        <td style={{ padding: '12px 8px', textAlign: 'right' }}>{typeof trade.entry === 'number' ? trade.entry.toFixed(4) : trade.entry}</td>
                                        <td style={{ padding: '12px 8px', textAlign: 'right' }}>{typeof trade.actual === 'number' ? trade.actual.toFixed(4) : trade.actual}</td>
                                        <td style={{ padding: '12px 8px', textAlign: 'right', color: trade.return >= 0 ? '#2e7d32' : '#c62828', fontWeight: '600' }}>{typeof trade.return === 'number' ? (trade.return >= 0 ? '+' : '') + (trade.return * 100).toFixed(2) + '%' : trade.return}</td>
                                        <td style={{ padding: '12px 8px', textAlign: 'right', color: trade.cumulativeReturn >= 1 ? '#2e7d32' : '#c62828' }}>{typeof trade.cumulativeReturn === 'number' ? ((trade.cumulativeReturn - 1) * 100).toFixed(2) + '%' : trade.cumulativeReturn}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {mode === 3 && (
                    <div style={{ height: '300px' }}>
                      {mode3Series.length === 0 ? (
                        <div className="text-center text-muted py-4">
                          <div>No prediction score data for the selected filters.</div>
                          <div className="small mt-2">This chart shows model prediction scores over time.</div>
                        </div>
                      ) : (
                        <Chart options={mode3Options} series={mode3Series} type={mode3Series?.[0]?.type || 'line'} height={300} />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
      {isCleanMode && (
        <>
          <div className="forecastnest-cleanbar">forecastnest.com | AI Crypto Forecasting Platform</div>
          <div className="forecastnest-watermark">forecastnest.com | AI Crypto Forecasting Platform</div>
        </>
      )}
    </>
  );
}